// App.js — FlagIQ v4.25.2 (per-user persistence + store screen)
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { App as CapacitorAppPlugin } from "@capacitor/app";
import FLAGS from "./flags";
import { LANGS, t, ti } from "./i18n";
import { HINT_IDS, HINT_INVENTORY_KEYS } from "./hints";

import Header from "./components/Header";
import HomeScreen from "./components/HomeScreen";
import LevelScreen from "./components/LevelScreen";
import GameScreen from "./components/GameScreen";
import LocalPackLevelsScreen from "./components/LocalPackLevelsScreen";
import LocalPacksGrid from "./components/LocalPacksGrid";
import AuthModal from "./components/AuthModal";
import SettingsModal from "./components/SettingsModal";
import { LockedModal, NoLivesModal } from "./components/Modals";
import StoreScreen from "./components/StoreScreen";
import ResetPasswordPage from "./components/ResetPasswordPage";
import LegalPage from "./components/LegalPage";
import {
  registerPurchaseRewardHandler,
  restorePurchases,
  runIapStartupDiagnostics,
} from "./purchases";
import IapDiagnosticsPanel from "./components/IapDiagnosticsPanel";
import { IS_DEBUG_BUILD, IS_HIDDEN_DEBUGGER_ENABLED } from "./debugTools";
import {
  LOCAL_PACKS,
  buildLocalPackLevels,
  getLocalLevelStars,
  getLocalPackProgress,
  READY_LOCAL_PACK_IDS,
} from "./localPacks";

// 🔹 NEW: Supabase client import
import {
  missingSupabaseEnv,
  supabase,
  restoreSupabaseSession,
  subscribeToSupabaseAuth,
} from "./supabaseClient";
import {
  ensurePlayerState,
  getPlayerState,
  updatePlayerState,
} from "./playerStateApi";
import { resolveFlagImageSrc } from "./flagAssets";
import {
  getAnonymousDeviceState,
  getOrCreateAnonymousDeviceId,
  saveAnonymousDeviceState,
  trackAnonymousDevice,
  linkAnonymousDeviceToUser,
  logAnonymousTrackingContext,
} from "./anonymousDeviceApi";
import { trackUserCountry } from "./countryTrackingApi";


const LANGUAGE_STORAGE_KEY = "flagLang";
const LEGACY_LANGUAGE_KEYS = ["flagiq:lang"];
const SUPPORTED_LANGUAGE_CODES = new Set(LANGS.map((l) => l.code));
const DEBUG_LOGS_STORAGE_KEY = "flagiq:debugLogs";
const DEBUG_LOGS_MAX = 100;
const StoreKitPurchase = registerPlugin("StoreKitPurchase");
const CapacitorApp = registerPlugin("App");
const DEEP_LINK_SCHEME = "flagiq";
const RESET_DEEP_LINK = `${DEEP_LINK_SCHEME}://reset-password`;
const AUTH_CALLBACK_DEEP_LINK = `${DEEP_LINK_SCHEME}://auth-callback`;

function parseSupabaseRecoveryUrl(url) {
  const parsed = {
    isResetRoute: false,
    mode: "unknown",
    type: "unknown",
    tokenType: "",
    code: "",
    accessToken: "",
    refreshToken: "",
    expiresAt: "",
    expiresIn: "",
    tokenHash: "",
    hasCode: false,
    hasAccessToken: false,
    hasRefreshToken: false,
    hasTokenHash: false,
  };
  if (!url) return parsed;

  try {
    const normalized = String(url).replace(`${DEEP_LINK_SCHEME}://`, "https://flagiq.local/");
    const u = new URL(normalized);
    const routePath = (u.pathname || "").replace(/\/+$/, "");
    const deepLinkRoutePath = `${u.host}${routePath}`.replace(/^\/+/, "");
    parsed.isResetRoute = routePath === "/reset-password" || deepLinkRoutePath === "reset-password";

    const hashParams = new URLSearchParams((u.hash || "").replace(/^#/, ""));
    const queryType = (u.searchParams.get("type") || "").toLowerCase();
    const hashType = (hashParams.get("type") || "").toLowerCase();
    const type = hashType || queryType;
    parsed.type = type || "unknown";
    parsed.tokenType = hashParams.get("token_type") || u.searchParams.get("token_type") || "";
    parsed.expiresAt = hashParams.get("expires_at") || u.searchParams.get("expires_at") || "";
    parsed.expiresIn = hashParams.get("expires_in") || u.searchParams.get("expires_in") || "";

    parsed.code = u.searchParams.get("code") || u.searchParams.get("token") || hashParams.get("code") || "";
    parsed.accessToken =
      hashParams.get("access_token") ||
      u.searchParams.get("access_token") ||
      "";
    parsed.refreshToken =
      hashParams.get("refresh_token") ||
      u.searchParams.get("refresh_token") ||
      "";
    parsed.tokenHash =
      u.searchParams.get("token_hash") ||
      hashParams.get("token_hash") ||
      "";

    parsed.hasCode = Boolean(parsed.code);
    parsed.hasAccessToken = Boolean(parsed.accessToken);
    parsed.hasRefreshToken = Boolean(parsed.refreshToken);
    parsed.hasTokenHash = Boolean(parsed.tokenHash);

    if (parsed.hasAccessToken && parsed.hasRefreshToken) {
      parsed.mode = "tokens";
    } else if (parsed.hasCode) {
      parsed.mode = "code";
    } else if (parsed.hasTokenHash) {
      parsed.mode = "otp";
    }
  } catch (e) {
    // ignore parse failures
  }

  return parsed;
}

function isRecoveryCandidateUrl(url) {
  const parsed = parseSupabaseRecoveryUrl(url);
  const hasRecoveryParams = parsed.mode !== "none" && parsed.mode !== "unknown";
  const isRecoveryType = parsed.type === "recovery";
  return {
    parsed,
    isRecoveryFlow: parsed.isResetRoute || hasRecoveryParams || (isRecoveryType && hasRecoveryParams),
  };
}

function sanitizeDeepLink(url) {
  if (!url) return "";
  try {
    const normalized = String(url).replace(`${DEEP_LINK_SCHEME}://`, "https://flagiq.local/");
    const parsed = new URL(normalized);
    const mask = (value) => (value ? "***" : "");
    parsed.searchParams.forEach((_, key) => {
      if (/(token|code|refresh|access)/i.test(key)) parsed.searchParams.set(key, mask(parsed.searchParams.get(key)));
    });
    const hashParams = new URLSearchParams((parsed.hash || "").replace(/^#/, ""));
    hashParams.forEach((value, key) => {
      if (/(token|code|refresh|access)/i.test(key)) hashParams.set(key, mask(value));
    });
    const hash = hashParams.toString();
    return `${DEEP_LINK_SCHEME}://${parsed.pathname.replace(/^\//,"")}${parsed.search}${hash ? `#${hash}` : ""}`;
  } catch (e) {
    return String(url);
  }
}


function normalizeLanguageCode(code) {
  const raw = String(code || "").toLowerCase();
  return SUPPORTED_LANGUAGE_CODES.has(raw) ? raw : "en";
}

function persistLanguageToStorage(langCode) {
  const normalized = normalizeLanguageCode(langCode);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    LEGACY_LANGUAGE_KEYS.forEach((legacyKey) => {
      localStorage.removeItem(legacyKey);
    });
  } catch (e) {}
  return normalized;
}

function readLanguageFromStorage() {
  const keys = [LANGUAGE_STORAGE_KEY, ...LEGACY_LANGUAGE_KEYS];
  for (const key of keys) {
    try {
      const value = localStorage.getItem(key);
      if (value) {
        return persistLanguageToStorage(value);
      }
    } catch (e) {}
  }
  return "en";
}

function readDebugLogs() {
  try {
    const raw = localStorage.getItem(DEBUG_LOGS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (e) {
    return [];
  }
}

function getAuthUserLabel(user) {
  if (!user) return "";
  return (
    user.user_metadata?.display_name ||
    user.user_metadata?.username ||
    user.email ||
    user.id ||
    ""
  );
}

function persistDebugLogs(logs) {
  try {
    localStorage.setItem(DEBUG_LOGS_STORAGE_KEY, JSON.stringify(logs));
  } catch (e) {
    // ignore
  }
}

function formatLogValue(value) {
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message || String(value);
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
}

// ----- constants -----
export const TOTAL_LEVELS = 55;
export const STARS_PER_LEVEL_MAX = 3;
export const BATCH = 5;
export const UNLOCK_THRESHOLD = 0.8;
export const BLOCK_REQUIRE = {
  5: 0,
  10: 12,
  15: 24,
  20: 36,
  25: 48,
  30: 60,
  35: 72,
  40: 84,
  45: 96,
  50: 108,
  55: 120,
};

export const MAX_HEARTS = 5;
export const REGEN_MS = 10 * 60 * 1000;
export const DEFAULT_HEARTS_STATE = {
  current: MAX_HEARTS,
  max: MAX_HEARTS,
  lastRegenAt: null,
};
const DAILY_SPIN_COOLDOWN_MS = 24 * 60 * 60 * 1000;

// ----- helpers -----
export const shuffle = (arr) =>
  arr
    .map((v) => [Math.random(), v])
    .sort((a, b) => a[0] - b[0])
    .map((x) => x[1]);
export const pickN = (arr, n) => shuffle([...arr]).slice(0, n);
export const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const remainingDailySpinMs = (lastClaimedAt) => {
  if (!lastClaimedAt) return 0;
  const parsed = Date.parse(lastClaimedAt);
  if (!Number.isFinite(parsed)) return 0;
  const elapsed = Date.now() - parsed;
  if (elapsed < 0) return DAILY_SPIN_COOLDOWN_MS;
  return Math.max(DAILY_SPIN_COOLDOWN_MS - elapsed, 0);
};

export const flagSrc = resolveFlagImageSrc;

export const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const hashPwd = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  return (h >>> 0).toString(16);
};
export const starsFromLives = (l) => clamp(l, 0, 3);
export const sumStars = (m) =>
  Object.values(m || {}).reduce((a, v) => a + (Number(v) || 0), 0);

export function computeUnlockedLevels(starsMap) {
  let unlocked = BATCH;
  while (unlocked < TOTAL_LEVELS) {
    const maxStarsPossible = unlocked * STARS_PER_LEVEL_MAX;
    const have = sumStars(starsMap);
    const ratio = maxStarsPossible > 0 ? have / maxStarsPossible : 0;
    if (ratio >= UNLOCK_THRESHOLD)
      unlocked = Math.min(unlocked + BATCH, TOTAL_LEVELS);
    else break;
  }
  return unlocked;
}

function normalizeModeKey(rawMode) {
  const m = String(rawMode || "").toLowerCase();
  if (m === "timetrial" || m === "time trial") return "timetrial";
  if (m === "localflags" || m === "local flags" || m === "local")
    return "local";
  return m || "classic";
}

function normalizeProgress(raw) {
  const base = {
    classic: { starsByLevel: {}, unlockedUntil: 5 },
    timetrial: { starsByLevel: {}, unlockedUntil: 5 },
    localFlags: { packs: {} },
  };

  if (!raw) return base;

  let parsed = raw;

  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return base;
    }
  }

  if (!parsed || typeof parsed !== "object") return base;

  if (parsed.localFlags?.packs && typeof parsed.localFlags.packs === "object") {
    base.localFlags.packs = { ...parsed.localFlags.packs };
  }

  if (parsed.local && typeof parsed.local === "object") {
    const migratedPacks = {};
    Object.entries(parsed.local).forEach(([packId, packValue]) => {
      migratedPacks[packId] = {
        ...packValue,
        starsByLevel: { ...(packValue?.starsByLevel || {}) },
      };
    });
    base.localFlags.packs = {
      ...base.localFlags.packs,
      ...migratedPacks,
    };
  }

  Object.entries(parsed).forEach(([modeKey, value]) => {
    const normalisedMode = normalizeModeKey(modeKey);
    if (normalisedMode === "local") {
      if (value?.packs && typeof value.packs === "object") {
        base.localFlags.packs = { ...value.packs };
      }
      return;
    }

    const target = base[normalisedMode];
    if (!target || !value || typeof value !== "object") return;

    if (value.starsByLevel && typeof value.starsByLevel === "object") {
      target.starsByLevel = { ...value.starsByLevel };
    }

    if (Number.isFinite(value.unlockedUntil)) {
      target.unlockedUntil = value.unlockedUntil;
    }
  });

  return base;
}


function hasMeaningfulProgress(progress) {
  const base = normalizeProgress(progress);
  const classicStars = Object.values(base.classic?.starsByLevel || {}).some(
    (stars) => Number(stars) > 0
  );
  const timetrialStars = Object.values(base.timetrial?.starsByLevel || {}).some(
    (stars) => Number(stars) > 0
  );
  const localStars = Object.values(base.localFlags?.packs || {}).some((pack) =>
    Object.values(pack?.starsByLevel || {}).some((stars) => Number(stars) > 0)
  );
  return classicStars || timetrialStars || localStars;
}

function hasMeaningfulPlayerState(state) {
  if (!state) return false;
  if (hasMeaningfulProgress(state.progress)) return true;
  if (Number(state.coins) > 0) return true;
  const { cleaned, legacyHints } = normalizeInventory(
    state.inventory || state.inventory_state || state.items || {}
  );
  const hints = cleaned?.hints || cleaned?.boosters || legacyHints || {};
  if (
    Object.entries(hints || {}).some(([key, qty]) => {
      const baseline = Number(DEFAULT_HINTS[key]) || 0;
      return Number(qty) > baseline;
    })
  ) {
    return true;
  }
  if (Number(state.hearts_max) > MAX_HEARTS) return true;
  return false;
}

function mergeInventory(primary, secondary) {
  const { cleaned: first } = normalizeInventory(primary);
  const { cleaned: second } = normalizeInventory(secondary);
  return {
    ...second,
    ...first,
    hints: {
      ...(second?.hints || {}),
      ...(first?.hints || {}),
    },
  };
}

function mergeProgress(primary, secondary) {
  const first = normalizeProgress(primary);
  const second = normalizeProgress(secondary);
  const merged = normalizeProgress(first);

  ["classic", "timetrial"].forEach((modeKey) => {
    const firstMode = first[modeKey] || {};
    const secondMode = second[modeKey] || {};
    const starsByLevel = { ...(firstMode.starsByLevel || {}) };
    Object.entries(secondMode.starsByLevel || {}).forEach(([level, stars]) => {
      starsByLevel[level] = Math.max(Number(starsByLevel[level]) || 0, Number(stars) || 0);
    });
    merged[modeKey] = {
      starsByLevel,
      unlockedUntil: Math.max(
        Number(firstMode.unlockedUntil) || 5,
        Number(secondMode.unlockedUntil) || 5
      ),
    };
  });

  const mergedPacks = { ...(first.localFlags?.packs || {}) };
  Object.entries(second.localFlags?.packs || {}).forEach(([packId, pack]) => {
    const existing = mergedPacks[packId] || { starsByLevel: {} };
    const starsByLevel = { ...(existing.starsByLevel || {}) };
    Object.entries(pack?.starsByLevel || {}).forEach(([level, stars]) => {
      starsByLevel[level] = Math.max(Number(starsByLevel[level]) || 0, Number(stars) || 0);
    });
    mergedPacks[packId] = {
      ...existing,
      ...pack,
      starsByLevel,
      currentLevel: Math.max(Number(existing.currentLevel) || 1, Number(pack?.currentLevel) || 1),
    };
  });
  merged.localFlags = { packs: mergedPacks };

  return merged;
}

export function starsNeededForLevelId(levelId, starsMap) {
  const blockEnd = Math.min(Math.ceil(levelId / BATCH) * BATCH, TOTAL_LEVELS);
  const required = BLOCK_REQUIRE[blockEnd] ?? 0;
  const have = sumStars(starsMap);
  return {
    need: Math.max(0, required - have),
    blockStart: blockEnd - (BATCH - 1),
    blockEnd,
  };
}

export function lastCompletedLevel(starsMap) {
  let last = 0;
  for (let i = 1; i <= TOTAL_LEVELS; i++) if ((starsMap[i] || 0) > 0) last = i;
  return last || 1;
}

function targetDifficultyForLevel(i) {
  const t0 = (i - 1) / (TOTAL_LEVELS - 1);
  const eased = t0 * t0 * t0;
  return 1 + eased * 8.5;
}

// NEW: minimum difficulty floor per level
function minDiffForLevel(i) {
  // 1–5: anything
  if (i <= 5) return 1;
  // 6–10: no diff-1
  if (i <= 10) return 2;
  // 11–15
  if (i <= 15) return 3;
  // 16–20 (England & similar won't appear)
  if (i <= 20) return 4;
  // 21–25
  if (i <= 25) return 5;
  // 26–30: only pretty hard stuff
  if (i <= 30) return 6;
  // 31–35: harder pool
  if (i <= 35) return 7;
  // 36–40: toughest pool
  if (i <= 40) return 8;
  // 41–55: elite pool
  return 9;
}

function capForLevel(i) {
  if (i <= 3) return 2.5;
  if (i <= 5) return 3.5;
  if (i <= 10) return 5.0;
  if (i <= 15) return 6.5;
  if (i <= 20) return 7.5;
  if (i <= 25) return 8.5;
  if (i <= 30) return 9.5;
  if (i <= 40) return 9.8;
  return 10.0;
}

export function buildLevels(flags) {
  const levels = [];
  const byDiff = [...flags].sort((a, b) => a.difficulty - b.difficulty);

  for (let i = 1; i <= TOTAL_LEVELS; i++) {
    const cap = capForLevel(i);
    const minDiff = minDiffForLevel(i);
    const target = targetDifficultyForLevel(i);

    // only flags within [minDiff, cap]
    let candidates = byDiff.filter(
      (f) =>
        typeof f.difficulty === "number" &&
        f.difficulty >= minDiff &&
        f.difficulty <= cap
    );

    // safety net – if for some reason too few candidates, fall back
    if (candidates.length < 40) candidates = byDiff;

    const nearest = [...candidates]
      .sort(
        (a, b) =>
          Math.abs(a.difficulty - target) - Math.abs(b.difficulty - target)
      )
      .slice(0, Math.min(120, candidates.length));

    let pool = pickN(nearest, Math.min(20, nearest.length));
    if (pool.length < 20) {
      const remain = candidates.filter(
        (f) => !pool.some((p) => p.code === f.code)
      );
      pool = [
        ...pool,
        ...pickN(remain, Math.min(20 - pool.length, remain.length)),
      ];
    }
    if (pool.length === 0) pool = pickN(byDiff, Math.min(20, byDiff.length));
    levels.push({ id: i, pool, questionCount: 10 });
  }
  return levels;
}

function normalizeHeartsState(raw) {
  const base = raw && typeof raw === "object" ? raw : {};

  const maxRaw =
    base.max ?? base.hearts_max ?? base.maxHearts ?? base.countMax ?? MAX_HEARTS;
  const max = Number.isFinite(Number(maxRaw)) && Number(maxRaw) > 0
    ? Number(maxRaw)
    : MAX_HEARTS;

  const currentRaw =
    base.current ?? base.hearts_current ?? base.count ?? base.hearts ?? max;
  const current = clamp(
    Number.isFinite(Number(currentRaw)) ? Number(currentRaw) : max,
    0,
    max
  );

  let last = base.lastRegenAt ?? base.hearts_last_regen_at ?? base.lastTick;
  if (last === undefined) last = null;
  let lastRegenAt = null;
  if (last !== null) {
    const numeric = Number(last);
    if (Number.isFinite(numeric)) {
      lastRegenAt = numeric;
    } else {
      const parsed = Date.parse(last);
      lastRegenAt = Number.isFinite(parsed) ? parsed : null;
    }
  }

  return { current, max, lastRegenAt };
}

function getHeartsStorageKey(username) {
  return username ? `flagiq:u:${username}:hearts` : null;
}

function getCooldownsStorageKey(username) {
  return username ? `flagiq:u:${username}:cooldowns` : null;
}

const REVIEW_PROMPT_DEFAULT = {
  lastReviewMilestonePrompted: 0,
  lastReviewPromptAt: 0,
  sessionsSinceLastPrompt: 0,
  totalReviewPromptAttempts: 0,
};

function normalizeReviewPromptState(raw) {
  if (!raw) return { ...REVIEW_PROMPT_DEFAULT };
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return { ...REVIEW_PROMPT_DEFAULT };
    }
  }
  if (!parsed || typeof parsed !== "object") {
    return { ...REVIEW_PROMPT_DEFAULT };
  }
  return {
    lastReviewMilestonePrompted: Number(parsed.lastReviewMilestonePrompted) || 0,
    lastReviewPromptAt: Number(parsed.lastReviewPromptAt) || 0,
    sessionsSinceLastPrompt: Number(parsed.sessionsSinceLastPrompt) || 0,
    totalReviewPromptAttempts: Number(parsed.totalReviewPromptAttempts) || 0,
  };
}

function mergeReviewPromptState(base, next) {
  return {
    lastReviewMilestonePrompted: Math.max(
      base.lastReviewMilestonePrompted || 0,
      next.lastReviewMilestonePrompted || 0
    ),
    lastReviewPromptAt: Math.max(
      base.lastReviewPromptAt || 0,
      next.lastReviewPromptAt || 0
    ),
    sessionsSinceLastPrompt: Math.max(
      base.sessionsSinceLastPrompt || 0,
      next.sessionsSinceLastPrompt || 0
    ),
    totalReviewPromptAttempts: Math.max(
      base.totalReviewPromptAttempts || 0,
      next.totalReviewPromptAttempts || 0
    ),
  };
}

function getReviewPromptStorageKey(username) {
  return username ? `flagiq:u:${username}:reviewPrompt` : null;
}

async function readCapacitorStorage(key) {
  if (!key || !Capacitor?.Plugins) return null;
  const plugin = Capacitor.Plugins.Preferences || Capacitor.Plugins.Storage;
  if (!plugin?.get) return null;
  try {
    const result = await plugin.get({ key });
    if (!result?.value) return null;
    return normalizeReviewPromptState(result.value);
  } catch (e) {
    return null;
  }
}

async function writeCapacitorStorage(key, value) {
  if (!key || !Capacitor?.Plugins) return;
  const plugin = Capacitor.Plugins.Preferences || Capacitor.Plugins.Storage;
  if (!plugin?.set) return;
  try {
    await plugin.set({ key, value: JSON.stringify(value) });
  } catch (e) {}
}

function useReviewPromptState(username) {
  const key = getReviewPromptStorageKey(username);
  const [state, setState] = useState(() => {
    if (!key) return { ...REVIEW_PROMPT_DEFAULT };
    try {
      const raw = localStorage.getItem(key);
      return normalizeReviewPromptState(raw);
    } catch (e) {
      return { ...REVIEW_PROMPT_DEFAULT };
    }
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!key) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const fromCapacitor = await readCapacitorStorage(key);
      if (!cancelled && fromCapacitor) {
        setState((prev) => mergeReviewPromptState(prev, fromCapacitor));
      }
      if (!cancelled) setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);

  useEffect(() => {
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (e) {}
    writeCapacitorStorage(key, state);
  }, [key, state]);

  return { state, setState, loaded };
}

function countUniqueCompletedLevels(progress) {
  const base = normalizeProgress(progress);
  const completed = new Set();
  Object.values(base).forEach((modeProgress) => {
    const starsByLevel = modeProgress?.starsByLevel || {};
    Object.entries(starsByLevel).forEach(([levelKey, stars]) => {
      if (Number(stars) > 0) {
        const parsed = Number(levelKey);
        if (Number.isFinite(parsed)) {
          completed.add(parsed);
        }
      }
    });
  });
  return completed.size;
}

const InAppReview = registerPlugin("InAppReview");

async function requestInAppReview() {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    await InAppReview.requestReview();
    return true;
  } catch (e) {
    return false;
  }
}

export function applyHeartsRegen(state, now = Date.now()) {
  const base = normalizeHeartsState(state);
  const ts = Number.isFinite(Number(now)) ? Number(now) : Date.now();

  if (base.current >= base.max) {
    return { ...base, lastRegenAt: null, nextRefreshAt: null, added: 0 };
  }

  if (base.lastRegenAt === null) {
    return { ...base, nextRefreshAt: null, added: 0 };
  }

  const elapsedMs = Math.max(0, ts - base.lastRegenAt);
  const elapsedMins = Math.floor(elapsedMs / 60000);
  const add = Math.floor(elapsedMins / 10);

  if (add <= 0) {
    const remainder = REGEN_MS - (elapsedMs % REGEN_MS || 0) || REGEN_MS;
    return {
      ...base,
      nextRefreshAt: ts + remainder,
      added: 0,
    };
  }

  const newCurrent = Math.min(base.max, base.current + add);
  const filled = newCurrent >= base.max;
  const nextLast = filled ? null : base.lastRegenAt + add * REGEN_MS;
  const nextRefreshAt = filled ? null : nextLast + REGEN_MS;

  return {
    current: newCurrent,
    max: base.max,
    lastRegenAt: nextLast,
    nextRefreshAt,
    added: newCurrent - base.current,
  };
}

function loadHeartsForUser(username) {
  const key = getHeartsStorageKey(username);
  if (!key) return DEFAULT_HEARTS_STATE;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return DEFAULT_HEARTS_STATE;
    const parsed = JSON.parse(raw);
    return normalizeHeartsState(parsed);
  } catch (e) {
    return DEFAULT_HEARTS_STATE;
  }
}

// ---- storage hooks ----
function useLocalStorage(key, init) {
  const [val, setVal] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      return v ? JSON.parse(v) : init;
    } catch (e) {
      return init;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(val));
    } catch (e) {}

  }, [key, val]);
  return [val, setVal];
}

function useUserStorage(username, suffix, init) {
  const key = username ? `flagiq:u:${username}:${suffix}` : null;
  const [val, setVal] = useState(init);
  useEffect(() => {
    if (!key) return;
    try {
      const v = localStorage.getItem(key);
      setVal(v ? JSON.parse(v) : init);
    } catch (e) {
      setVal(init);
    }
  }, [username, suffix]);
  useEffect(() => {
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(val));
    } catch (e) {}

  }, [key, val]);
  return [val, setVal];
}

function deriveModeStatsFromProgress(progress, mode) {
  const starsMap = progress?.[mode]?.starsByLevel || {};
  const totalStars = sumStars(starsMap);
  if (totalStars === 0) {
    return { level: 0, stars: 0 };
  }
  return {
    level: lastCompletedLevel(starsMap),
    stars: totalStars,
  };
}

// ----- per-user hints hook (with migration from old keys) -----
const DEFAULT_HINTS = {
  [HINT_INVENTORY_KEYS[HINT_IDS.REMOVE_TWO]]: 3,
  [HINT_INVENTORY_KEYS[HINT_IDS.AUTO_PASS]]: 1,
  [HINT_INVENTORY_KEYS[HINT_IDS.PAUSE_TIMER]]: 2,
};

const LEGACY_HINT_KEY_MAP = {
  "Remove Two": HINT_INVENTORY_KEYS[HINT_IDS.REMOVE_TWO],
  InstantCorrect: HINT_INVENTORY_KEYS[HINT_IDS.AUTO_PASS],
  "Extra Time": HINT_INVENTORY_KEYS[HINT_IDS.PAUSE_TIMER],
};

function normalizeInventory(rawInventory) {
  const base = rawInventory && typeof rawInventory === "object" ? rawInventory : {};

  const legacyHints = {};
  const cleanedEntries = Object.entries(base).filter(([key, value]) => {
    if (LEGACY_HINT_KEY_MAP[key]) {
      if (Number.isFinite(value)) {
        legacyHints[LEGACY_HINT_KEY_MAP[key]] = value;
      }
      return false;
    }
    return true;
  });

  return {
    cleaned: Object.fromEntries(cleanedEntries),
    legacyHints,
  };
}

function loadHintsForUser(username) {
  try {
    if (username) {
      const userKey = `flagiq:u:${username}:hints`;
      const rawUser = localStorage.getItem(userKey);
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed && typeof parsed === "object") {
          return {
            ...DEFAULT_HINTS,
            ...parsed,
          };
        }
      }

      // migrate from legacy keys on first run
      const legacyRaw =
        localStorage.getItem("flag_hints") || localStorage.getItem("hints");
      if (legacyRaw) {
        const legacyParsed = JSON.parse(legacyRaw);
        const merged = {
          ...DEFAULT_HINTS,
          ...(legacyParsed && typeof legacyParsed === "object"
            ? legacyParsed
            : {}),
        };
        localStorage.setItem(userKey, JSON.stringify(merged));
        return merged;
      }

      return DEFAULT_HINTS;
    }

    // no username: just fall back to legacy/global if present
    const legacyRaw =
      localStorage.getItem("flag_hints") || localStorage.getItem("hints");
    if (legacyRaw) {
      const legacyParsed = JSON.parse(legacyRaw);
      return {
        ...DEFAULT_HINTS,
        ...(legacyParsed && typeof legacyParsed === "object"
          ? legacyParsed
          : {}),
      };
    }
  } catch (e) {
    // ignore
  }
  return DEFAULT_HINTS;
}

function usePerUserHints(username) {
  const [hints, setHints] = useState(() => loadHintsForUser(username));

  // reload when user changes (login/switch)
  useEffect(() => {
    const next = loadHintsForUser(username);
    setHints(next);
  }, [username]);

  // persist whenever hints change
  useEffect(() => {
    if (!username) return;
    try {
      localStorage.setItem(`flagiq:u:${username}:hints`, JSON.stringify(hints));
    } catch (e) {
      // ignore
    }
  }, [username, hints]);

  return [hints, setHints];
}

// ================= Main App =================
function isResetPasswordRoute() {
  if (typeof window === "undefined") return false;
  return window.location.pathname === "/reset-password";
}

function getLegalRoute() {
  if (typeof window === "undefined") return "";
  const path = window.location.pathname;
  if (path === "/terms") return "terms";
  if (path === "/privacy") return "privacy";
  return "";
}

function ConfigMissingScreen({ missingKeys }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "#0b74ff",
        color: "white",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: 520 }}>
        <h1 style={{ fontSize: 22, marginBottom: 12 }}>Configuration Missing</h1>
        <p style={{ color: "#cbd5f5", marginBottom: 16 }}>
          The app cannot start because required environment variables are not
          defined.
        </p>
        <div
          style={{
            background: "rgba(15, 23, 42, 0.8)",
            border: "1px solid rgba(148, 163, 184, 0.4)",
            borderRadius: 12,
            padding: 16,
            textAlign: "left",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 13,
          }}
        >
          {missingKeys.map((key) => (
            <div key={key}>• {key}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AuthBootScreen() {
  const poweredByLabel = "Powered by Wild Moustache Games";

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0b74ff",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
        }}
      >
        <div
          style={{
            width: 120,
            height: 120,
            borderRadius: 28,
            background: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
          }}
        >
          <img
            src="/icon-512.png"
            alt="FlagIQ"
            style={{
              width: 90,
              height: 90,
              borderRadius: 22,
              boxShadow: "0 8px 18px rgba(0,0,0,0.25)",
            }}
          />
        </div>
        <div
          style={{
            fontSize: 34,
            fontWeight: 900,
            letterSpacing: -0.5,
            color: "#fff",
            textShadow: "0 3px 10px rgba(0,0,0,0.35)",
          }}
        >
          FlagIQ
        </div>
        <div
          style={{
            padding: "12px 20px",
            borderRadius: 999,
            background: "rgba(15, 23, 42, 0.75)",
            color: "#fff",
            fontWeight: 700,
            letterSpacing: 0.4,
            boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
          }}
        >
          Loading…
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          left: "50%",
          bottom: "max(20px, calc(env(safe-area-inset-bottom, 0px) + 12px))",
          transform: "translateX(-50%)",
          color: "rgba(255,255,255,0.88)",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0.2,
          textAlign: "center",
          textShadow: "0 1px 4px rgba(0,0,0,0.2)",
          padding: "0 16px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {poweredByLabel}
      </div>
    </div>
  );
}

export default function App() {
  const legalRoute = getLegalRoute();
  if (legalRoute) {
    return <LegalPage page={legalRoute} />;
  }

  if (missingSupabaseEnv.length > 0) {
    return <ConfigMissingScreen missingKeys={missingSupabaseEnv} />;
  }

  const debugOverlayEnabled = IS_HIDDEN_DEBUGGER_ENABLED;
  const initialDebugLogs = useMemo(
    () => (debugOverlayEnabled ? readDebugLogs() : []),
    [debugOverlayEnabled]
  );
  const [debugLogs, setDebugLogs] = useState(initialDebugLogs);
  const [debugError, setDebugError] = useState(initialDebugLogs[0] || null);
  const [showDebugOverlay, setShowDebugOverlay] = useState(false);
  const [showDebugScreen, setShowDebugScreen] = useState(false);
  const debugTapRef = useRef({ count: 0, timer: null });
  const [appInfo, setAppInfo] = useState({ version: "", build: "" });
  const [gameplayDiagnostics, setGameplayDiagnostics] = useState(null);
  const [questionFlowDiagnostics, setQuestionFlowDiagnostics] = useState(null);
  const [resetDeepLinkUrl, setResetDeepLinkUrl] = useState("");
  const [isRecoveryFlow, setIsRecoveryFlow] = useState(() => {
    if (typeof window === "undefined") return false;
    return isRecoveryCandidateUrl(window.location.href).isRecoveryFlow;
  });
  const [resetDiagnostics, setResetDiagnostics] = useState({
    lastDeepLinkReceived: "",
    parsedType: "unknown",
    hasAccessToken: false,
    hasRefreshToken: false,
    hasCode: false,
    hasTokenHash: false,
    recoveryPath: "none",
    didExchangeOrSetSessionSucceed: false,
    sessionPresentAfterRecovery: false,
    recoveryResult: "none",
    lastResetError: "",
  });

  // preferences
  const [lang, setLang] = useState(() => readLanguageFromStorage());
  const [backendPreferredLanguage, setBackendPreferredLanguage] = useState(null);
  const [pendingPreferredLanguagePush, setPendingPreferredLanguagePush] =
    useState(false);
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine !== false
  );
  const [soundOn, setSoundOn] = useLocalStorage("flagiq:soundOn", true);
  const [volume] = useLocalStorage("flagiq:volume", 0.5);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [audioUnlockResult, setAudioUnlockResult] = useState(null);
  const [audioDiagLogs, setAudioDiagLogs] = useState([]);
  const [audioDiagTick, setAudioDiagTick] = useState(0);
  const [audioSessionInfo, setAudioSessionInfo] = useState({
    category: "unknown",
    mode: "unknown",
    active: false,
  });

  const [screen, setScreen] = useLocalStorage("flagiq:screen", "home");
  const [mode, setMode] = useLocalStorage("flagiq:mode", "classic");
  const defaultLocalPackId = LOCAL_PACKS[0]?.packId || "";

  // Always reset scroll so headers stay visible when switching screens (mobile fix)
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo(0, 0);
  }, [screen]);

  const getDebugRoute = useCallback(() => {
    if (typeof window === "undefined") return screen || "unknown";
    const path = window.location?.pathname || "/";
    const screenLabel = screen ? `screen:${screen}` : "";
    return screenLabel ? `${path} (${screenLabel})` : path;
  }, [screen]);

  const captureDebugError = useCallback(
    (error, meta = {}) => {
      if (!debugOverlayEnabled) return;
      const message =
        typeof error === "string"
          ? error
          : error?.message || "Unknown error";
      const stack =
        typeof error === "string" ? "" : error?.stack || meta.stack || "";
      const logEntry = {
        id: Date.now(),
        level: "error",
        message,
        stack,
        route: getDebugRoute(),
        timestamp: new Date().toISOString(),
        source: meta.source || "",
      };
      setDebugError(logEntry);
      setShowDebugOverlay(true);
      setDebugLogs((prev) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const next = [logEntry, ...safePrev].slice(0, DEBUG_LOGS_MAX);
        persistDebugLogs(next);
        return next;
      });
    },
    [debugOverlayEnabled, getDebugRoute]
  );

  useEffect(() => {
    runIapStartupDiagnostics();
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const plugin = Capacitor?.Plugins?.App;
        if (!plugin?.getInfo) return;
        const info = await plugin.getInfo();
        if (!mounted) return;
        setAppInfo({
          version: info?.version || "",
          build: info?.build || "",
        });
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const navigateToResetPassword = () => {
      if (window.location.pathname !== "/reset-password") {
        window.history.replaceState({}, "", "/reset-password");
      }
    };

    const processIncomingUrl = async (url) => {
      if (!url) return false;
      const isDeepLink = url.startsWith(RESET_DEEP_LINK) || url.startsWith(AUTH_CALLBACK_DEEP_LINK);
      const { parsed, isRecoveryFlow: isRecoveryCandidate } = isRecoveryCandidateUrl(url);
      console.log("[reset-password] appUrlOpen launch url", sanitizeDeepLink(url));
      console.log("[reset-password] appUrlOpen parsed", {
        type: parsed.type,
        mode: parsed.mode,
        hasAccessToken: parsed.hasAccessToken,
        hasRefreshToken: parsed.hasRefreshToken,
        expiresAt: Boolean(parsed.expiresAt),
        expiresIn: Boolean(parsed.expiresIn),
        tokenType: Boolean(parsed.tokenType),
      });
      if (!isDeepLink && !isRecoveryCandidate) return false;

      const next = {
        lastDeepLinkReceived: sanitizeDeepLink(url),
        parsedType: parsed.type,
        hasAccessToken: parsed.hasAccessToken,
        hasRefreshToken: parsed.hasRefreshToken,
        hasCode: parsed.hasCode,
        hasTokenHash: parsed.hasTokenHash,
        recoveryPath: parsed.mode,
        didExchangeOrSetSessionSucceed: false,
        sessionPresentAfterRecovery: false,
        recoveryResult: "none",
        lastResetError: "",
      };

      if (isRecoveryCandidate) {
        next.recoveryResult = "pending:handledByResetPage";
      } else {
        next.recoveryResult = "ignored:notRecovery";
      }

      if (isRecoveryCandidate) {
        navigateToResetPassword();
        setIsRecoveryFlow(true);
        setResetDeepLinkUrl(url);
      }
      setResetDiagnostics(next);
      return true;
    };

    const currentCandidate = isRecoveryCandidateUrl(window.location.href);
    if (currentCandidate.isRecoveryFlow) {
      const currentUrl = window.location.href;
      const parsedCurrentUrl = currentCandidate.parsed;
      navigateToResetPassword();
      setIsRecoveryFlow(true);
      setResetDeepLinkUrl(currentUrl);
      setResetDiagnostics((prev) => ({
        ...prev,
        lastDeepLinkReceived: sanitizeDeepLink(currentUrl),
        parsedType: parsedCurrentUrl.type,
        hasAccessToken: parsedCurrentUrl.hasAccessToken,
        hasRefreshToken: parsedCurrentUrl.hasRefreshToken,
        hasCode: parsedCurrentUrl.hasCode,
        hasTokenHash: parsedCurrentUrl.hasTokenHash,
        recoveryPath: parsedCurrentUrl.mode,
        recoveryResult: "pending:handledByResetPage",
      }));
    }

    const platform =
      typeof Capacitor?.getPlatform === "function"
        ? Capacitor.getPlatform()
        : "web";
    const supportsNativeAppUrlEvents =
      platform !== "web" &&
      CapacitorAppPlugin &&
      typeof CapacitorAppPlugin.getLaunchUrl === "function" &&
      typeof CapacitorAppPlugin.addListener === "function";

    if (!supportsNativeAppUrlEvents) {
      return () => {};
    }

    let listener;
    CapacitorAppPlugin.getLaunchUrl()
      .then((result) => processIncomingUrl(result?.url || ""))
      .catch(() => {});

    CapacitorAppPlugin.addListener("appUrlOpen", (data) => {
      processIncomingUrl(data?.url || "");
    })
      .then((handle) => {
        listener = handle;
      })
      .catch(() => {});

    return () => {
      listener?.remove?.();
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__FLIQ_DEBUG_CAPTURE = captureDebugError;
    return () => {
      if (window.__FLIQ_DEBUG_CAPTURE === captureDebugError) {
        delete window.__FLIQ_DEBUG_CAPTURE;
      }
    };
  }, [captureDebugError]);

  useEffect(() => {
    if (!debugOverlayEnabled || typeof console === "undefined") return;
    const original = {
      log: console.log,
      warn: console.warn,
      error: console.error,
    };
    const recordLog = (level, args) => {
      const message = args.map(formatLogValue).join(" ");
      const logEntry = {
        id: Date.now() + Math.random(),
        level,
        message,
        route: getDebugRoute(),
        timestamp: new Date().toISOString(),
      };
      setDebugLogs((prev) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const next = [logEntry, ...safePrev].slice(0, DEBUG_LOGS_MAX);
        persistDebugLogs(next);
        return next;
      });
    };
    console.log = (...args) => {
      original.log(...args);
      recordLog("log", args);
    };
    console.warn = (...args) => {
      original.warn(...args);
      recordLog("warn", args);
    };
    console.error = (...args) => {
      original.error(...args);
      recordLog("error", args);
    };
    return () => {
      console.log = original.log;
      console.warn = original.warn;
      console.error = original.error;
    };
  }, [debugOverlayEnabled, getDebugRoute]);

  const handleCopyDebugError = useCallback(() => {
    if (!debugError || typeof document === "undefined") return;
    const infoLine =
      appInfo.version || appInfo.build
        ? `App: ${appInfo.version || "unknown"} (${
            appInfo.build || "unknown"
          })`
        : null;
    const logs = debugLogs.slice(0, 20).map((log) => {
      const level = log.level ? log.level.toUpperCase() : "LOG";
      return `[${level}] ${log.timestamp || ""} ${log.message || ""}`.trim();
    });
    const payload = [
      infoLine,
      `Message: ${debugError.message || "Unknown error"}`,
      `Route: ${debugError.route || "unknown"}`,
      `Time: ${debugError.timestamp || ""}`,
      debugError.source ? `Source: ${debugError.source}` : null,
      debugError.stack ? `Stack:\n${debugError.stack}` : null,
      logs.length > 0 ? `Recent logs:\n${logs.join("\n")}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(payload).catch(() => {});
      return;
    }
    const textarea = document.createElement("textarea");
    textarea.value = payload;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
    } catch (e) {
      // ignore
    }
    document.body.removeChild(textarea);
  }, [appInfo, debugError, debugLogs]);

  const handleDebugTap = useCallback(() => {
    if (!debugOverlayEnabled) return;
    const ref = debugTapRef.current;
    ref.count += 1;
    if (ref.timer) {
      clearTimeout(ref.timer);
    }
    ref.timer = setTimeout(() => {
      ref.count = 0;
    }, 1200);
    if (ref.count >= 7) {
      ref.count = 0;
      setShowDebugScreen(true);
    }
  }, [debugOverlayEnabled]);

  useEffect(() => {
    if (!debugOverlayEnabled || typeof window === "undefined") return;
    const handleError = (event) => {
      if (event?.error) {
        captureDebugError(event.error, {
          stack: event.error?.stack,
          source: event.filename,
        });
        return;
      }
      captureDebugError(event?.message || "Unknown error", {
        stack: "",
        source: event?.filename,
      });
    };
    const handleRejection = (event) => {
      const reason = event?.reason;
      if (reason instanceof Error) {
        captureDebugError(reason);
        return;
      }
      captureDebugError(
        typeof reason === "string" ? reason : "Unhandled promise rejection",
        { stack: reason?.stack }
      );
    };
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, [captureDebugError, debugOverlayEnabled]);

  useEffect(() => {
    return () => {
      const ref = debugTapRef.current;
      if (ref.timer) {
        clearTimeout(ref.timer);
      }
    };
  }, []);

  useEffect(() => {
    if (IS_HIDDEN_DEBUGGER_ENABLED) return;
    if (showDebugScreen || showDebugOverlay) {
      console.warn("[debug-tools] Debug UI attempted to mount in production; force-disabling.");
      setShowDebugScreen(false);
      setShowDebugOverlay(false);
    }
  }, [showDebugOverlay, showDebugScreen]);

  const [users, setUsers] = useLocalStorage("flagiq:users", {});
  const [activeUser, setActiveUser] = useLocalStorage("flagiq:activeUser", "");
  const [activeUserLabel, setActiveUserLabel] = useLocalStorage(
    "flagiq:activeUserLabel",
    ""
  );
  const [lastCreds, setLastCreds] = useLocalStorage("flagiq:lastCreds", {});
  const loggedIn = !!activeUser;
  const [guestSessionActive, setGuestSessionActive] = useState(false);
  const hasAppSession = loggedIn || guestSessionActive;
  const storageUserId = loggedIn ? activeUser : guestSessionActive ? "guest" : "";
  const [guestPromptedMilestones, setGuestPromptedMilestones] = useLocalStorage(
    "flagiq:guestPromptedMilestones",
    []
  );
  const [guestPromptMilestone, setGuestPromptMilestone] = useState(0);
  const [pendingAuthAction, setPendingAuthAction] = useState("");
  const [anonymousDeviceId, setAnonymousDeviceId] = useState("");
  const [authReady, setAuthReady] = useState(!supabase);
  const [backendLoaded, setBackendLoaded] = useState(false);


  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        logAnonymousTrackingContext("app start tracking effect started", {
          guestSessionActive,
          loggedIn,
          activeUser: activeUser || null,
        });
        const deviceId = await getOrCreateAnonymousDeviceId();
        if (cancelled) return;
        setAnonymousDeviceId(deviceId);
        const tracked = await trackAnonymousDevice(deviceId);
        await trackUserCountry({ anonymousDeviceId: deviceId });
        logAnonymousTrackingContext("app start tracking effect completed", {
          device_id: deviceId,
          last_seen_at: tracked?.last_seen_at || null,
          row: tracked,
        });
      } catch (e) {
        console.warn("Anonymous device tracking failed", e);
        logAnonymousTrackingContext("app start tracking effect failed", { error: e });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!anonymousDeviceId || !activeUser) return;
    logAnonymousTrackingContext("authenticated user link tracking effect started", {
      device_id: anonymousDeviceId,
      activeUser,
    });
    linkAnonymousDeviceToUser(anonymousDeviceId, activeUser)
      .then(async (tracked) => {
        await trackUserCountry({ anonymousDeviceId, userId: activeUser });
        logAnonymousTrackingContext("authenticated user link tracking effect completed", {
          device_id: anonymousDeviceId,
          activeUser,
          last_seen_at: tracked?.last_seen_at || null,
          row: tracked,
        });
      })
      .catch((e) => {
        console.warn("Anonymous device link failed", e);
        logAnonymousTrackingContext("authenticated user link tracking effect failed", {
          device_id: anonymousDeviceId,
          activeUser,
          error: e,
        });
      });
  }, [anonymousDeviceId, activeUser]);

  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    (async () => {
      try {
        if (isRecoveryFlow || isResetPasswordRoute()) {
          console.log("[reset-password] user/profile fetch blocked because recovery flow is active", {
            isRecoveryFlow,
            isResetRoute: isResetPasswordRoute(),
          });
          if (!cancelled) setAuthReady(true);
          return;
        }
        await restoreSupabaseSession();
        if (cancelled) return;
        const { data, error } = await supabase.auth.getUser();
        if (error) throw error;
        const user = data?.user;
        if (user && !cancelled) {
          setActiveUser(user.id || user.email || "");
          setActiveUserLabel(getAuthUserLabel(user));
        } else if (!cancelled) {
          setActiveUser("");
          setActiveUserLabel("");
        }
      } catch (e) {
        if (!cancelled) {
          setActiveUser("");
          setActiveUserLabel("");
        }
      } finally {
        if (!cancelled) {
          setAuthReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isRecoveryFlow, setActiveUser, setActiveUserLabel, setAuthReady]);

  useEffect(() => {
    if (!supabase) return;
    const unsubscribe = subscribeToSupabaseAuth((event, session) => {
      const isRecoveryEvent = event === "PASSWORD_RECOVERY";
      const shouldIgnoreAuthMutation =
        isRecoveryFlow || isRecoveryEvent || isResetPasswordRoute();

      if (isRecoveryEvent) {
        setIsRecoveryFlow(true);
        if (typeof window !== "undefined" && window.location.pathname !== "/reset-password") {
          window.history.replaceState({}, "", "/reset-password");
        }
      }

      if (shouldIgnoreAuthMutation) {
        return;
      }

      if (event === "SIGNED_OUT" || !session?.user) {
        setActiveUser("");
        setActiveUserLabel("");
        return;
      }

      const user = session.user;
      setActiveUser(user.id || user.email || "");
      setActiveUserLabel(getAuthUserLabel(user));
    });
    return () => {
      unsubscribe();
    };
  }, [isRecoveryFlow, setActiveUser, setActiveUserLabel]);

  const handleLanguageChange = useCallback(
    (next) => {
      const normalized = normalizeLanguageCode(next);
      setLang(normalized);
      if (activeUser) {
        setPendingPreferredLanguagePush(true);
      }
    },
    [activeUser]
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    persistLanguageToStorage(lang);
  }, [lang]);


  useEffect(() => {
    if (activeUser && !activeUserLabel) {
      setActiveUserLabel(activeUser);
    }
  }, [activeUser, activeUserLabel, setActiveUserLabel]);

  useEffect(() => {
    if (loggedIn) {
      setGuestSessionActive(false);
    }
  }, [loggedIn]);


  // remember where to go back to when leaving the store
  const [lastNonStoreScreen, setLastNonStoreScreen] = useState("home");

  // per-user data
  const [levelId, setLevelId] = useUserStorage(storageUserId, `${mode}:level`, 1);
  const [activeLocalPackId, setActiveLocalPackId] = useUserStorage(
    storageUserId,
    "localFlags:pack",
    defaultLocalPackId
  );
  const [progress, setProgress] = useState(() => normalizeProgress());
  const progressStorageKey = storageUserId
    ? `flagiq:progress:${storageUserId}`
    : null;
  const {
    state: reviewPromptState,
    setState: setReviewPromptState,
    loaded: reviewPromptLoaded,
  } = useReviewPromptState(storageUserId);
  const reviewSessionIncrementedRef = useRef(false);

  useEffect(() => {
    if (!storageUserId) {
      setProgress(normalizeProgress());
      return;
    }
    try {
      const raw = localStorage.getItem(progressStorageKey);
      setProgress(normalizeProgress(raw));
    } catch (e) {
      setProgress(normalizeProgress());
    }
  }, [progressStorageKey, storageUserId]);

  useEffect(() => {
    if (!progressStorageKey) return;
    try {
      localStorage.setItem(progressStorageKey, JSON.stringify(progress));
    } catch (e) {}
  }, [progress, progressStorageKey]);

  useEffect(() => {
    reviewSessionIncrementedRef.current = false;
  }, [storageUserId]);

  useEffect(() => {
    if (!storageUserId || !reviewPromptLoaded) return;
    if (reviewSessionIncrementedRef.current) return;
    setReviewPromptState((prev) => ({
      ...prev,
      sessionsSinceLastPrompt: (prev.sessionsSinceLastPrompt || 0) + 1,
    }));
    reviewSessionIncrementedRef.current = true;
  }, [reviewPromptLoaded, setReviewPromptState, storageUserId]);

  const maybePromptForReview = useCallback(
    (nextProgress) => {
      if (!loggedIn || !activeUser || !reviewPromptLoaded) return;
      const uniqueCompleted = countUniqueCompletedLevels(nextProgress);
      const milestone = Math.floor(uniqueCompleted / 5) * 5;
      const eligible =
        milestone >= 5 &&
        milestone > (reviewPromptState.lastReviewMilestonePrompted || 0);
      if (!eligible) return;
      if ((reviewPromptState.totalReviewPromptAttempts || 0) >= 3) return;
      const lastPromptAt = reviewPromptState.lastReviewPromptAt || 0;
      const daysSincePrompt =
        lastPromptAt > 0
          ? (Date.now() - lastPromptAt) / (1000 * 60 * 60 * 24)
          : Infinity;
      if (daysSincePrompt < 7) return;
      if ((reviewPromptState.sessionsSinceLastPrompt || 0) < 3) return;

      (async () => {
        const reviewRequested = await requestInAppReview();
        if (!reviewRequested) return;
        setReviewPromptState((prev) => ({
          ...prev,
          lastReviewMilestonePrompted: milestone,
          lastReviewPromptAt: Date.now(),
          sessionsSinceLastPrompt: 0,
          totalReviewPromptAttempts: (prev.totalReviewPromptAttempts || 0) + 1,
        }));
      })();
    },
    [activeUser, loggedIn, reviewPromptLoaded, reviewPromptState, setReviewPromptState]
  );

  const maybePromptGuestAccount = useCallback(
    (nextProgress) => {
      if (!guestSessionActive || loggedIn) return;
      const uniqueCompleted = countUniqueCompletedLevels(nextProgress);
      const milestone = Math.floor(uniqueCompleted / 5) * 5;
      if (milestone < 5) return;
      if (guestPromptMilestone === milestone) return;
      if (guestPromptedMilestones.includes(milestone)) return;
      setGuestPromptMilestone(milestone);
    },
    [guestPromptMilestone, guestPromptedMilestones, guestSessionActive, loggedIn]
  );

  const persistProgress = useCallback(
    (nextProgress) => {
      if (!progressStorageKey) return;
      try {
        localStorage.setItem(progressStorageKey, JSON.stringify(nextProgress));
      } catch (e) {}
    },
    [progressStorageKey]
  );





  // 🔁 HINTS: now use dedicated per-user hook (with legacy migration)
  const [hints, setHints] = usePerUserHints(storageUserId);

  // Backend inventory (includes hints). We keep a copy so we can merge
  // additional keys the backend might have without losing them when we
  // update hints.
  const [inventory, setInventory] = useState(null);
  const [cooldowns, setCooldowns] = useState({});

  // 🔑 COINS: single source of truth synced with localStorage
  const [coins, setCoins] = useState(0);
  const [heartsState, setHeartsState] = useState(DEFAULT_HEARTS_STATE);
  const backendHeartsRef = useRef(null);
  const pendingHeartsUpdateRef = useRef(null);
  const heartsPushTimeoutRef = useRef(null);
  const [nextHeartsRefreshAt, setNextHeartsRefreshAt] = useState(null);

  const buildAnonymousGuestStatePayload = useCallback(
    (nextProgress = progress) => ({
      coins,
      preferred_language: normalizeLanguageCode(lang),
      progress: normalizeProgress(nextProgress),
      inventory: { hints: { ...(hints || {}) } },
      cooldowns: cooldowns || {},
      hearts_current: heartsState?.current ?? MAX_HEARTS,
      hearts_max: heartsState?.max ?? MAX_HEARTS,
      hearts_last_regen_at: heartsState?.lastRegenAt
        ? new Date(heartsState.lastRegenAt).toISOString()
        : null,
    }),
    [coins, cooldowns, heartsState, hints, lang, progress]
  );

  const saveAnonymousGuestState = useCallback(
    async (reason, nextProgress = progress) => {
      if (!guestSessionActive || loggedIn || !anonymousDeviceId || !backendLoaded) {
        logAnonymousTrackingContext("guest state save skipped", {
          reason,
          guestSessionActive,
          loggedIn,
          anonymous_device_id: anonymousDeviceId || null,
          backendLoaded,
        });
        return null;
      }

      const payload = buildAnonymousGuestStatePayload(nextProgress);
      logAnonymousTrackingContext("guest state save payload sent to Supabase", {
        reason,
        anonymous_device_id: anonymousDeviceId,
        payload,
      });

      try {
        const response = await saveAnonymousDeviceState(anonymousDeviceId, payload);
        logAnonymousTrackingContext("guest state save Supabase response", {
          reason,
          anonymous_device_id: anonymousDeviceId,
          payload,
          response,
          error: null,
        });
        return response;
      } catch (error) {
        console.warn("Anonymous guest state save failed", error);
        logAnonymousTrackingContext("guest state save Supabase error", {
          reason,
          anonymous_device_id: anonymousDeviceId,
          payload,
          response: null,
          error,
        });
        return null;
      }
    },
    [
      anonymousDeviceId,
      backendLoaded,
      buildAnonymousGuestStatePayload,
      guestSessionActive,
      loggedIn,
      progress,
    ]
  );

  const updateProgressAfterLevel = useCallback(
    (modeKey, level, stars) => {
      setProgress((prev) => {
        const base = normalizeProgress(prev);
        const normalizedMode = normalizeModeKey(modeKey);
        let next = base;

        if (normalizedMode === "local") {
          const packId = activeLocalPackId || LOCAL_PACKS[0]?.packId;
          if (!packId) return base;
          const currentLocal = base.localFlags?.packs || {};
          const pack = currentLocal[packId] || { starsByLevel: {} };
          const prevStars = Number(pack.starsByLevel?.[level] || 0);
          const best = Math.max(prevStars, Number(stars || 0));
          next = {
            ...base,
            localFlags: {
              ...base.localFlags,
              packs: {
                ...currentLocal,
                [packId]: {
                  ...pack,
                  starsByLevel: {
                    ...(pack.starsByLevel || {}),
                    [level]: best,
                  },
                  currentLevel: level,
                  updatedAt: new Date().toISOString(),
                },
              },
            },
          };
        } else {
          const modeProgress = base[normalizedMode] || {
            starsByLevel: {},
            unlockedUntil: BATCH,
          };
          const prevStars = Number(modeProgress.starsByLevel?.[level] || 0);
          const best = Math.max(prevStars, Number(stars || 0));
          const updatedStars = { ...modeProgress.starsByLevel, [level]: best };
          const unlocked =
            normalizedMode === "classic" || normalizedMode === "timetrial"
              ? computeUnlockedLevels(updatedStars)
              : Number.isFinite(Number(modeProgress.unlockedUntil))
              ? Number(modeProgress.unlockedUntil)
              : 0;
          next = {
            ...base,
            [normalizedMode]: {
              starsByLevel: updatedStars,
              unlockedUntil: unlocked,
            },
          };
        }
        persistProgress(next);
        if (activeUser) {
          updatePlayerState(activeUser, { progress: next });
        } else {
          saveAnonymousGuestState("level_completion_after_persistProgress", next);
        }
        maybePromptForReview(next);
        maybePromptGuestAccount(next);
        return next;
      });
    },
    [
      activeLocalPackId,
      activeUser,
      maybePromptForReview,
      maybePromptGuestAccount,
      persistProgress,
      saveAnonymousGuestState,
    ]
  );

  useEffect(() => {
    backendHeartsRef.current = null;
    pendingHeartsUpdateRef.current = null;
    setNextHeartsRefreshAt(null);
  }, [storageUserId]);

  useEffect(() => {
    if (!storageUserId) {
      setBackendPreferredLanguage(null);
      setPendingPreferredLanguagePush(false);
      setPendingAuthAction("");
      setCoins(0);
      setHeartsState(DEFAULT_HEARTS_STATE);
      setBackendLoaded(false);
      setInventory(null);
      setCooldowns({});
      return;
    }
    if (!loggedIn) {
      setBackendPreferredLanguage(null);
      setPendingPreferredLanguagePush(false);
      setBackendLoaded(true);
      setInventory(null);
      try {
        const raw = localStorage.getItem(`flagiq:u:${storageUserId}:coins`);
        setCoins(raw ? Number(raw) : 0);
      } catch (e) {
        setCoins(0);
      }
      setHeartsState(loadHeartsForUser(storageUserId));
      try {
        const cooldownRaw = localStorage.getItem(getCooldownsStorageKey(storageUserId));
        setCooldowns(cooldownRaw ? JSON.parse(cooldownRaw) : {});
      } catch (e) {
        setCooldowns({});
      }
      return;
    }
    setBackendLoaded(false);
    setCooldowns({});
    setHeartsState(loadHeartsForUser(storageUserId));

    const authAction = pendingAuthAction;
    (async () => {
      try {
        if (authAction !== "login") {
          await ensurePlayerState(activeUser);
        }

        const state = await getPlayerState(activeUser);
        if (state) {
          setCoins(Number(state.coins) || 0);

          const { cleaned: backendInventory, legacyHints } = normalizeInventory(
            state.inventory || state.inventory_state || state.items || {}
          );

          setInventory(backendInventory);

          const backendHints =
            (backendInventory && backendInventory.hints) ||
            (backendInventory && backendInventory.boosters);
          if (backendHints && typeof backendHints === "object") {
            setHints((prev) => ({
              ...DEFAULT_HINTS,
              ...prev,
              ...backendHints,
              ...legacyHints,
            }));
          } else if (Object.keys(legacyHints).length > 0) {
            setHints((prev) => ({
              ...DEFAULT_HINTS,
              ...prev,
              ...legacyHints,
            }));
          }

          const backendProgress = normalizeProgress(state.progress);
          setProgress(backendProgress);
          persistProgress(backendProgress);

          const backendLang =
            state.preferred_language ||
            state.preferred_lang ||
            state.lang ||
            state.language ||
            "";
          const normalizedBackendLang = backendLang
            ? normalizeLanguageCode(backendLang)
            : "";
          if (normalizedBackendLang) {
            setBackendPreferredLanguage(normalizedBackendLang);
            setPendingPreferredLanguagePush(false);
            if (normalizedBackendLang !== lang) {
              setLang(normalizedBackendLang);
            } else {
              persistLanguageToStorage(normalizedBackendLang);
            }
          } else {
            setBackendPreferredLanguage(null);
            const storedLang = normalizeLanguageCode(
              readLanguageFromStorage() || lang
            );
            if (storedLang !== lang) {
              setLang(storedLang);
            }
            setPendingPreferredLanguagePush(true);
          }

          const backendHearts = normalizeHeartsState({
            hearts_current: state.hearts_current,
            hearts_max: state.hearts_max,
            hearts_last_regen_at: state.hearts_last_regen_at,
          });
          const regenerated = applyHeartsRegen(backendHearts, Date.now());
          backendHeartsRef.current = backendHearts;
          setHeartsState(regenerated);
          if (regenerated.added > 0) {
            pendingHeartsUpdateRef.current = regenerated;
          }
          try {
            localStorage.setItem(
              getHeartsStorageKey(storageUserId),
              JSON.stringify({
                hearts_current: regenerated.current,
                hearts_max: regenerated.max,
                hearts_last_regen_at: regenerated.lastRegenAt,
              })
            );
          } catch (e) {}

          const backendCooldowns = state.cooldowns || {};
          setCooldowns(backendCooldowns);
        }

        setBackendLoaded(true);
        setPendingAuthAction("");
      } catch (e) {
        // fallback to local only
        try {
          const raw = localStorage.getItem(`flagiq:u:${storageUserId}:coins`);
          setCoins(raw ? Number(raw) : 0);
        } catch (e) {}
        setHeartsState(loadHeartsForUser(storageUserId));
        setCooldowns({});
        setBackendPreferredLanguage((prev) => prev);
        setPendingPreferredLanguagePush((prev) => prev || !!activeUser);

        // ✅ allow later sync back to backend
        setBackendLoaded(true);
        setPendingAuthAction("");
      }
    })();
  }, [activeUser, loggedIn, pendingAuthAction, persistProgress, storageUserId]);


  // helper to update coins AND persist to localStorage
  const applyCoinsUpdate = useCallback(
    (valueOrUpdater) => {
      setCoins((prev) => {
        const next =
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(prev)
            : valueOrUpdater;
        const safe = Number.isFinite(Number(next)) ? Number(next) : 0;
        if (storageUserId) {
          try {
            localStorage.setItem(`flagiq:u:${storageUserId}:coins`, String(safe));
          } catch (e) {}
        }
        return safe;
      });
    },
    [storageUserId]
  );

  const flushHeartsUpdate = useCallback(async () => {
    if (
      !activeUser ||
      !backendLoaded ||
      !pendingHeartsUpdateRef.current ||
      !isOnline
    ) {
      return;
    }

    const payloadState = normalizeHeartsState(pendingHeartsUpdateRef.current);
    pendingHeartsUpdateRef.current = null;
    try {
      const updated = await updatePlayerState(activeUser, {
        hearts_current: payloadState.current,
        hearts_max: payloadState.max,
        hearts_last_regen_at: payloadState.lastRegenAt
          ? new Date(payloadState.lastRegenAt).toISOString()
          : null,
      });
      const normalizedUpdated = normalizeHeartsState({
        hearts_current: updated?.hearts_current ?? payloadState.current,
        hearts_max: updated?.hearts_max ?? payloadState.max,
        hearts_last_regen_at:
          updated?.hearts_last_regen_at ?? payloadState.lastRegenAt,
      });
      backendHeartsRef.current = normalizedUpdated;
      const recomputed = applyHeartsRegen(normalizedUpdated, Date.now());
      setHeartsState((prev) => ({
        ...normalizeHeartsState(prev),
        ...recomputed,
      }));
      setNextHeartsRefreshAt(recomputed.nextRefreshAt ?? null);
    } catch (e) {
      pendingHeartsUpdateRef.current = payloadState;
    }
  }, [activeUser, backendLoaded, isOnline]);

  const queueHeartsUpdate = useCallback(
    (state) => {
      if (!activeUser || !backendLoaded) return;
      pendingHeartsUpdateRef.current = normalizeHeartsState(state);
      if (heartsPushTimeoutRef.current) return;
      heartsPushTimeoutRef.current = setTimeout(() => {
        heartsPushTimeoutRef.current = null;
        flushHeartsUpdate();
      }, 200);
    },
    [activeUser, backendLoaded, flushHeartsUpdate]
  );

  useEffect(() => {
    registerPurchaseRewardHandler(async (product) => {
      const reward = product?.reward || {};
      let coinsGranted = 0;
      let heartsRefilled = false;

      if (Number.isFinite(Number(reward.coins))) {
        coinsGranted = Number(reward.coins);
        if (coinsGranted > 0) {
          applyCoinsUpdate((prev) => Math.max(0, prev + coinsGranted));
        }
      }

      if (reward.heartsRefill) {
        heartsRefilled = true;
        setHeartsState((prev) => {
          const normalized = normalizeHeartsState(prev);
          const nextState = {
            ...normalized,
            current: normalized.max,
            max: normalized.max,
            lastRegenAt: null,
          };
          queueHeartsUpdate(nextState);
          return nextState;
        });
      }

      return { success: true, coinsGranted, heartsRefilled };
    });
  }, [applyCoinsUpdate, queueHeartsUpdate]);

  const handleDailySpinClaim = useCallback(async () => {
    if (!storageUserId) {
      return { success: false, reason: "not_ready" };
    }

    if (loggedIn && typeof navigator !== "undefined" && navigator.onLine === false) {
      return { success: false, reason: "offline" };
    }

    if (!loggedIn) {
      const currentCooldowns = cooldowns || {};
      const lastClaimedAt = currentCooldowns?.dailySpin?.last_claimed_at || null;
      const remainingMs = remainingDailySpinMs(lastClaimedAt);
      if (remainingMs > 0) {
        return { success: false, remainingMs, lastClaimedAt };
      }
      const nextIso = new Date().toISOString();
      const mergedCooldowns = {
        ...currentCooldowns,
        dailySpin: {
          ...(currentCooldowns.dailySpin || {}),
          last_claimed_at: nextIso,
        },
      };
      setCooldowns(mergedCooldowns);
      try {
        localStorage.setItem(
          getCooldownsStorageKey(storageUserId),
          JSON.stringify(mergedCooldowns)
        );
      } catch (e) {}
      return { success: true, lastClaimedAt: nextIso };
    }

    try {
      const latestState = await getPlayerState(activeUser);
      const latestCooldowns = latestState?.cooldowns || {};
      setCooldowns(latestCooldowns);

      const lastClaimedAt =
        latestCooldowns?.dailySpin?.last_claimed_at || null;
      const remainingMs = remainingDailySpinMs(lastClaimedAt);
      if (remainingMs > 0) {
        return { success: false, remainingMs, lastClaimedAt };
      }

      const nextIso = new Date().toISOString();
      const mergedCooldowns = {
        ...latestCooldowns,
        dailySpin: {
          ...(latestCooldowns.dailySpin || {}),
          last_claimed_at: nextIso,
        },
      };

      const updated = await updatePlayerState(activeUser, {
        cooldowns: mergedCooldowns,
      });
      const savedCooldowns = updated?.cooldowns || mergedCooldowns;
      setCooldowns(savedCooldowns);

      const savedLast =
        savedCooldowns?.dailySpin?.last_claimed_at || nextIso;

      return { success: true, lastClaimedAt: savedLast };
    } catch (error) {
      console.error("Failed to update daily spin cooldown", error);
      return { success: false, reason: "error" };
    }
  }, [activeUser, backendLoaded, cooldowns, loggedIn, storageUserId]);

  useEffect(() => {
    flushHeartsUpdate();
  }, [flushHeartsUpdate]);

  useEffect(() => {
    if (!activeUser || !backendLoaded) return;

    updatePlayerState(activeUser, {
      coins,
    });
  }, [coins, activeUser, backendLoaded]);

  // Persist hints to backend inventory, keeping any other inventory keys intact
  useEffect(() => {
    if (!activeUser || !backendLoaded) return;

    setInventory((prev) => {
      const { cleaned } = normalizeInventory(prev);
      const next = { ...cleaned, hints };
      updatePlayerState(activeUser, { inventory: next });
      return next;
    });
  }, [hints, activeUser, backendLoaded]);

  useEffect(() => {
    if (!activeUser || !backendLoaded) return;

    const normalized = normalizeLanguageCode(lang);
    if (
      backendPreferredLanguage === normalized &&
      !pendingPreferredLanguagePush
    ) {
      return;
    }

    if (!isOnline) {
      setPendingPreferredLanguagePush(true);
      return;
    }

    (async () => {
      try {
        await updatePlayerState(activeUser, {
          preferred_language: normalized,
        });
        setBackendPreferredLanguage(normalized);
        setPendingPreferredLanguagePush(false);
      } catch (e) {
        setPendingPreferredLanguagePush(true);
      }
    })();
  }, [
    activeUser,
    backendLoaded,
    backendPreferredLanguage,
    isOnline,
    lang,
    pendingPreferredLanguagePush,
  ]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveAnonymousGuestState("debounced_guest_state_change", progress);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [progress, saveAnonymousGuestState]);

  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [lockInfo, setLockInfo] = useState(null);
  const [noLivesOpen, setNoLivesOpen] = useState(false);

  const [levels] = useState(() => buildLevels(FLAGS));
  const activeLocalPack = useMemo(() => {
    return (
      LOCAL_PACKS.find((pack) => pack.packId === activeLocalPackId) ||
      LOCAL_PACKS[0] ||
      null
    );
  }, [activeLocalPackId]);
  const localPackLevels = useMemo(
    () => (activeLocalPack ? buildLocalPackLevels(activeLocalPack) : []),
    [activeLocalPack]
  );
  const localMaxLevels = useMemo(() => {
    const readyPackIds = new Set(READY_LOCAL_PACK_IDS);
    return LOCAL_PACKS.filter((pack) => readyPackIds.has(pack.packId)).reduce(
      (total, pack) => total + buildLocalPackLevels(pack).length,
      0
    );
  }, []);
  const homeMaxLevels = useMemo(
    () => ({
      classic: levels.length,
      timetrial: levels.length,
      local: localMaxLevels,
    }),
    [levels.length, localMaxLevels]
  );
  const localLevelLabel = useMemo(() => {
    if (mode !== "local") return null;
    return localPackLevels.find((level) => level.id === levelId)?.label || null;
  }, [mode, localPackLevels, levelId]);
  const heartsCurrent = heartsState?.current ?? MAX_HEARTS;
  const heartsMax = heartsState?.max ?? MAX_HEARTS;
  const lastRegenAt = heartsState?.lastRegenAt ?? null;

  const refreshHeartsFromBackend = useCallback(async () => {
    if (!activeUser || !backendLoaded || !isOnline) return;
    try {
      const latest = await getPlayerState(activeUser);
      const backendHearts = normalizeHeartsState({
        hearts_current: latest?.hearts_current,
        hearts_max: latest?.hearts_max,
        hearts_last_regen_at: latest?.hearts_last_regen_at,
      });
      backendHeartsRef.current = backendHearts;
      const regenerated = applyHeartsRegen(backendHearts, Date.now());
      setHeartsState((prev) => ({
        ...normalizeHeartsState(prev),
        ...regenerated,
      }));
      setNextHeartsRefreshAt(regenerated.nextRefreshAt ?? null);
      if (regenerated.added > 0) {
        queueHeartsUpdate(regenerated);
      }
    } catch (e) {
      // ignore fetch errors
    }
  }, [activeUser, backendLoaded, isOnline, queueHeartsUpdate]);

  const applyHeartsTick = useCallback(
    (forcedNow) => {
      if (!hasAppSession) return;
      const nowTs = Number.isFinite(Number(forcedNow))
        ? Number(forcedNow)
        : Date.now();

      setHeartsState((prev) => {
        const normalized = normalizeHeartsState(prev);
        const regen = applyHeartsRegen(normalized, nowTs);
        const capSource =
          loggedIn && !isOnline && backendHeartsRef.current
            ? applyHeartsRegen(backendHeartsRef.current, nowTs)
            : null;

        const cappedCurrent =
          capSource && regen.current > capSource.current
            ? capSource.current
            : regen.current;
        const cappedLast =
          capSource && regen.current > capSource.current
            ? capSource.lastRegenAt
            : regen.lastRegenAt;
        const nextRefresh =
          regen.nextRefreshAt ?? capSource?.nextRefreshAt ?? null;

        const nextState = {
          ...normalized,
          ...regen,
          current: cappedCurrent,
          lastRegenAt: cappedLast,
          nextRefreshAt: nextRefresh || null,
        };

        const actualAdded = Math.max(0, cappedCurrent - normalized.current);
        if (actualAdded > 0 && loggedIn) {
          queueHeartsUpdate(nextState);
        }
        setNextHeartsRefreshAt(nextRefresh || null);
        return nextState;
      });
    },
    [hasAppSession, isOnline, loggedIn, queueHeartsUpdate]
  );

  useEffect(() => {
    if (!hasAppSession) return;
    applyHeartsTick();
    const id = setInterval(() => applyHeartsTick(), 1500);
    let timeoutId = null;
    if (nextHeartsRefreshAt) {
      const delay = Math.max(500, nextHeartsRefreshAt - Date.now());
      timeoutId = setTimeout(() => applyHeartsTick(), delay);
    }
    return () => {
      clearInterval(id);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [applyHeartsTick, hasAppSession, nextHeartsRefreshAt]);

  useEffect(() => {
    if (!activeUser || !backendLoaded) return;
    if (isOnline) {
      refreshHeartsFromBackend();
    }
  }, [activeUser, backendLoaded, isOnline, refreshHeartsFromBackend]);

  useEffect(() => {
    if (!activeUser) return;
    const handleFocus = () => refreshHeartsFromBackend();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        refreshHeartsFromBackend();
      }
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [activeUser, refreshHeartsFromBackend]);

  // home guard
  useEffect(() => {
    if (!authReady) return;
    if (!hasAppSession && screen !== "home") setScreen("home");
  }, [authReady, hasAppSession, screen, setScreen]);

  const goHome = () => setScreen("home");
  const goLevels = () => setScreen("levels");
  const goGame = () => setScreen("game");

  // ★ IMPORTANT: rely on LevelScreen for lock logic; just check hearts here
  function onLevelClick(id) {
    if (heartsCurrent <= 0) {
      setNoLivesOpen(true);
      return;
    }
    setLevelId(id);
    goGame();
  }

  function onRunLost() {
    setHeartsState((prev) => {
      const current = normalizeHeartsState(prev);
      const nextCurrent = Math.max(0, current.current - 1);
      const needsRegenStart =
        nextCurrent < current.max && current.lastRegenAt === null;
      const nextState = {
        ...current,
        current: nextCurrent,
        lastRegenAt: needsRegenStart ? Date.now() : current.lastRegenAt,
        nextRefreshAt: needsRegenStart
          ? Date.now() + REGEN_MS
          : current.nextRefreshAt ?? null,
      };
      queueHeartsUpdate(nextState);
      return nextState;
    });
  }

  const audioCtxRef = useRef(null);
  const activeTonesRef = useRef(0);
  const initAudioStartedRef = useRef(false);
  const isIOSPlatform = useMemo(() => {
    if (typeof Capacitor?.getPlatform === "function") {
      return Capacitor.getPlatform() === "ios";
    }
    return false;
  }, []);
  const ensureAudio = () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioCtxRef.current = new Ctx();
    }
    return audioCtxRef.current;
  };
  const pushAudioDiagLog = useCallback((message) => {
    const line = `${new Date().toISOString()} ${message}`;
    setAudioDiagLogs((prev) => [line, ...prev].slice(0, 30));
  }, []);

  const readAudioSessionStatus = useCallback(async () => {
    if (!isIOSPlatform || !StoreKitPurchase?.audioSessionStatus) return;
    try {
      const status = await StoreKitPurchase.audioSessionStatus();
      setAudioSessionInfo({
        category: status?.category || "unknown",
        mode: status?.mode || "unknown",
        active: Boolean(status?.active),
      });
    } catch (error) {
      pushAudioDiagLog(`audioSessionStatus failed: ${error?.message || String(error)}`);
    }
  }, [isIOSPlatform, pushAudioDiagLog]);

  const applyIosAudioSession = useCallback(async (enabled) => {
    if (!isIOSPlatform || !StoreKitPurchase?.audioSessionConfigure) return;
    try {
      const response = await StoreKitPurchase.audioSessionConfigure({ enabled: Boolean(enabled) });
      setAudioSessionInfo({
        category: response?.category || "unknown",
        mode: response?.mode || "unknown",
        active: Boolean(response?.active),
      });
      pushAudioDiagLog(`AVAudioSession category: ${response?.category || "unknown"} (enabled=${Boolean(enabled)})`);
    } catch (error) {
      pushAudioDiagLog(`audioSessionConfigure failed: ${error?.message || String(error)}`);
    }
  }, [isIOSPlatform, pushAudioDiagLog]);

  const unlockAudio = useCallback(async () => {
    try {
      const ctx = ensureAudio();
      if (!ctx) {
        const result = { ok: false, message: "AudioContext unavailable" };
        setAudioUnlockResult(result);
        pushAudioDiagLog(`Unlock Audio failed: ${result.message}`);
        return result;
      }
      if (ctx.state !== "running") {
        await ctx.resume();
      }
      const buffer = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * 0.08)), ctx.sampleRate);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      const gain = ctx.createGain();
      gain.gain.value = 0.0001;
      source.connect(gain).connect(ctx.destination);
      source.start(0);
      source.stop(ctx.currentTime + 0.08);
      await new Promise((resolve) => setTimeout(resolve, 100));
      const ok = ctx.state === "running";
      const result = { ok, message: ok ? "Audio unlocked" : `AudioContext state is ${ctx.state}` };
      setAudioUnlockResult(result);
      pushAudioDiagLog(`Unlock Audio ${ok ? "success" : "failed"}: ${result.message}`);
      return result;
    } catch (error) {
      const result = { ok: false, message: error?.message || String(error) };
      setAudioUnlockResult(result);
      pushAudioDiagLog(`Unlock Audio failed: ${result.message}`);
      return result;
    }
  }, [pushAudioDiagLog]);

  const initAudioOnce = useCallback(() => {
    if (initAudioStartedRef.current) return;
    initAudioStartedRef.current = true;
    const onFirstGesture = async () => {
      setHasUserInteracted(true);
      await unlockAudio();
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
      window.removeEventListener("click", onFirstGesture);
    };
    window.addEventListener("pointerdown", onFirstGesture, { passive: true });
    window.addEventListener("touchstart", onFirstGesture, { passive: true });
    window.addEventListener("click", onFirstGesture, { passive: true });
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        unlockAudio();
      }
    };
    const onFocus = () => unlockAudio();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
  }, [unlockAudio]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    initAudioOnce();
  }, [initAudioOnce]);

  useEffect(() => {
    const id = setInterval(() => setAudioDiagTick((prev) => prev + 1), 500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    applyIosAudioSession(soundOn);
    readAudioSessionStatus();
  }, [applyIosAudioSession, readAudioSessionStatus, soundOn]);

  useEffect(() => {
    if (!soundOn) return;
    const unlockAudio = () => {
      const ctx = ensureAudio();
      if (!ctx || ctx.state !== "suspended") return;
      ctx.resume().catch(() => {});
    };
    document.addEventListener("touchstart", unlockAudio, { passive: true });
    document.addEventListener("pointerdown", unlockAudio, { passive: true });
    document.addEventListener("mousedown", unlockAudio, { passive: true });
    document.addEventListener("keydown", unlockAudio, { passive: true });
    return () => {
      document.removeEventListener("touchstart", unlockAudio);
      document.removeEventListener("pointerdown", unlockAudio);
      document.removeEventListener("mousedown", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
    };
  }, [soundOn]);

  const tone = (f, d, type = "sine", delay = 0) => {
    if (!soundOn) return;
    if (isIOSPlatform && !hasUserInteracted) {
      pushAudioDiagLog("Blocked tone before iOS user interaction");
      return;
    }
    const ctx = ensureAudio();
    if (!ctx) return;
    const playTone = () => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.value = f;
      g.gain.value = volume;
      o.connect(g).connect(ctx.destination);
      const t0 = ctx.currentTime + delay;
      activeTonesRef.current += 1;
      o.onended = () => {
        activeTonesRef.current = Math.max(0, activeTonesRef.current - 1);
      };
      o.start(t0);
      o.stop(t0 + d);
    };
    if (ctx.state === "suspended") {
      ctx.resume().then(playTone).catch(() => {});
      return;
    }
    playTone();
  };
  const soundCorrect = () => {
    tone(523.25, 0.12, "triangle", 0.0);
    tone(659.25, 0.12, "triangle", 0.12);
    tone(783.99, 0.18, "triangle", 0.24);
  };
  const soundWrong = () => {
    tone(200, 0.1, "sawtooth");
  };
  const testMusic = () => {
    tone(261.63, 0.35, "sine", 0);
    tone(329.63, 0.35, "sine", 0.4);
    tone(392.0, 0.35, "sine", 0.8);
  };

  const openAuth = (tab = "login") => {
    setAuthTab(tab);
    setAuthOpen(true);
  };
  const closeAuth = () => setAuthOpen(false);
  const handleStartGuest = () => {
    setGuestSessionActive(true);
    setAuthOpen(false);
    setScreen("home");
  };

  const handleRestorePurchases = useCallback(async () => {
    const result = await restorePurchases();
    return result;
  }, []);

  const migrateGuestDataToAccount = useCallback(
    async (accountId, authAction = "login") => {
      if (!accountId || !guestSessionActive) return;
      try {
        await saveAnonymousDeviceState(anonymousDeviceId, {
          coins,
          preferred_language: normalizeLanguageCode(lang),
          progress: normalizeProgress(progress),
          inventory: { hints: { ...(hints || {}) } },
          cooldowns: cooldowns || {},
          hearts_current: heartsState?.current ?? MAX_HEARTS,
          hearts_max: heartsState?.max ?? MAX_HEARTS,
          hearts_last_regen_at: heartsState?.lastRegenAt
            ? new Date(heartsState.lastRegenAt).toISOString()
            : null,
        });
        await linkAnonymousDeviceToUser(anonymousDeviceId, accountId);
        await trackUserCountry({ anonymousDeviceId, userId: accountId });
        const anonymousState = await getAnonymousDeviceState(anonymousDeviceId).catch(() => null);
        const guestProgress = normalizeProgress(anonymousState?.progress || progress);
        const guestInventory = anonymousState?.inventory || { hints: { ...(hints || {}) } };
        const guestHearts = normalizeHeartsState({
          hearts_current: anonymousState?.hearts_current ?? heartsState?.current,
          hearts_max: anonymousState?.hearts_max ?? heartsState?.max,
          hearts_last_regen_at:
            anonymousState?.hearts_last_regen_at ?? heartsState?.lastRegenAt,
        });
        const existingState = await getPlayerState(accountId).catch(() => null);
        const accountHasProgress = hasMeaningfulPlayerState(existingState);

        if (authAction === "login" && accountHasProgress) {
          return;
        }

        if (!existingState) {
          await ensurePlayerState(accountId);
        }

        await updatePlayerState(accountId, {
          progress:
            authAction === "signup"
              ? guestProgress
              : mergeProgress(normalizeProgress(existingState?.progress), guestProgress),
          coins:
            authAction === "signup"
              ? Math.max(Number(anonymousState?.coins ?? coins) || 0, 0)
              : Math.max(Number(existingState?.coins) || 0, Number(anonymousState?.coins ?? coins) || 0),
          inventory:
            authAction === "signup"
              ? guestInventory
              : mergeInventory(existingState?.inventory, guestInventory),
          hearts_current: guestHearts.current,
          hearts_max: guestHearts.max,
          hearts_last_regen_at: guestHearts.lastRegenAt
            ? new Date(guestHearts.lastRegenAt).toISOString()
            : null,
          cooldowns: anonymousState?.cooldowns || cooldowns || {},
          preferred_language: normalizeLanguageCode(
            anonymousState?.preferred_language || lang
          ),
        });
      } catch (error) {
        console.error("Guest data migration failed", error);
      }
    },
    [anonymousDeviceId, coins, cooldowns, guestSessionActive, heartsState, hints, lang, progress]
  );

  // NEW: per-mode stats for homepage cards, based on in-memory progress
  const classicStats = deriveModeStatsFromProgress(progress, "classic");
  const timetrialStats = deriveModeStatsFromProgress(progress, "timetrial");

  const handleHomeStart = (nextMode, pack, interactionMeta = {}) => {
    const eventType = interactionMeta?.eventType || "unknown";
    const eventTarget = interactionMeta?.event?.target;
    const targetTag = eventTarget?.tagName || "unknown";
    const targetLabel =
      eventTarget?.getAttribute?.("aria-label") ||
      eventTarget?.textContent?.trim?.()?.slice(0, 60) ||
      "none";
    let navigationFired = false;

    if (!authReady) {
      if (IS_DEBUG_BUILD) {
        console.debug("[home-cta] blocked", {
          eventType,
          targetTag,
          targetLabel,
          currentRoute: screen,
          navigationFired,
          reason: "auth-not-ready",
        });
      }
      return;
    }
    if (!hasAppSession) {
      return;
    }
    if (nextMode === "local") {
      if (pack?.packId) {
        setActiveLocalPackId(pack.packId);
      }
      setMode("local");
      setScreen(pack ? "local-pack-levels" : "local-packs");
      navigationFired = true;
      if (IS_DEBUG_BUILD) {
        console.debug("[home-cta] navigate", {
          eventType,
          targetTag,
          targetLabel,
          currentRoute: screen,
          navigationFired,
          destination: pack ? "local-pack-levels" : "local-packs",
        });
      }
      return;
    }
    setMode(nextMode || "classic");
    setScreen("levels");
    navigationFired = true;
    if (IS_DEBUG_BUILD) {
      console.debug("[home-cta] navigate", {
        eventType,
        targetTag,
        targetLabel,
        currentRoute: screen,
        navigationFired,
        destination: "levels",
      });
    }
  };

  const handleLocalPackSelect = useCallback(
    (pack) => {
      if (!hasAppSession) return;
      if (pack?.packId) {
        setActiveLocalPackId(pack.packId);
      }
      setMode("local");
      setScreen("local-pack-levels");
    },
    [hasAppSession, setActiveLocalPackId, setMode, setScreen]
  );

  // navigation helper for opening the store from header
  const openStoreFromScreen = () => {
    setLastNonStoreScreen(screen || "levels");
    setScreen("shop");
  };

  // Buy a single extra heart using coins (prototype only)
  const handleBuyHeartWithCoins = () => {
    const HEART_COST = 50;
    if (heartsCurrent >= heartsMax) return;
    if (coins < HEART_COST) return;

    // deduct coins
    applyCoinsUpdate((prev) => {
      const next = Math.max(0, (Number(prev) || 0) - HEART_COST);
      return next;
    });

    // add +1 heart, capped at max
    setHeartsState((prev) => {
      const current = normalizeHeartsState(prev);
      const nextCurrent = Math.min(current.max, current.current + 1);
      const nextState = {
        ...current,
        current: nextCurrent,
        lastRegenAt: nextCurrent >= current.max ? null : current.lastRegenAt,
      };
      queueHeartsUpdate(nextState);
      return nextState;
    });
  };

  const modeProgress = mode === "local"
    ? { starsByLevel: {}, unlockedUntil: 0 }
    : progress[mode] || {
        starsByLevel: {},
        unlockedUntil: BATCH,
      };
  const starsByLevel = modeProgress.starsByLevel || {};
  const unlockedFromProgress =
    mode === "local"
      ? 0
      : Number.isFinite(Number(modeProgress.unlockedUntil))
      ? Number(modeProgress.unlockedUntil)
      : computeUnlockedLevels(starsByLevel || {});
  const localPackStarsTotal = useMemo(
    () =>
      LOCAL_PACKS.reduce((total, pack) => {
        const stats = getLocalPackProgress(pack, progress);
        return total + stats.starsEarned;
      }, 0),
    [progress]
  );
  const localFlagsLabelRaw = t && lang ? t(lang, "localFlags") : "Local Flags";
  const localFlagsLabel =
    localFlagsLabelRaw === "localFlags" ? "Local Flags" : localFlagsLabelRaw;
  const activeLevels = mode === "local" ? localPackLevels : levels;
  const storedStars =
    mode === "local"
      ? getLocalLevelStars(progress, activeLocalPackId, levelId)
      : Number(starsByLevel[levelId]) || 0;

  const handleGameBack = () => {
    if (mode === "local") {
      setScreen("local-pack-levels");
      return;
    }
    setScreen("levels");
  };


  const handleGameplayDiagnostics = useCallback((stats) => {
    setGameplayDiagnostics(stats);
  }, []);

  const handleQuestionFlowDiagnostics = useCallback((stats) => {
    setQuestionFlowDiagnostics(stats);
  }, []);
  const handleNextLevel = () => {
    if (mode === "local") {
      const idx = localPackLevels.findIndex((lvl) => lvl.id === levelId);
      const next = idx >= 0 ? localPackLevels[idx + 1] : null;
      if (next) {
        setLevelId(next.id);
        setScreen("game");
      } else {
        setScreen("local-pack-levels");
      }
      return;
    }

    const next = levelId + 1;
    const unlocked = Math.max(
      unlockedFromProgress,
      computeUnlockedLevels(starsByLevel || {})
    );
    if (next <= unlocked) {
      setLevelId(next);
      setScreen("game");
    } else {
      setScreen("levels");
    }
  };

  if (isRecoveryFlow || isResetPasswordRoute() || resetDeepLinkUrl) {
    return (
      <ResetPasswordPage
        recoveryUrl={resetDeepLinkUrl || (typeof window !== "undefined" ? window.location.href : "")}
        onDiagnosticsChange={(details) => {
          setResetDiagnostics((prev) => ({ ...prev, ...details }));
        }}
        onDone={() => {
          if (typeof window !== "undefined" && window.location.pathname === "/reset-password") {
            window.history.replaceState({}, "", "/");
          }
          setResetDeepLinkUrl("");
          setIsRecoveryFlow(false);
          setAuthTab("login");
          setAuthOpen(true);
          setScreen("home");
        }}
      />
    );
  }

  if (!authReady) {
    return <AuthBootScreen />;
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0b74ff" }}>

      {debugOverlayEnabled && (
        <div
          onClick={handleDebugTap}
          style={{
            position: "fixed",
            bottom: "env(safe-area-inset-bottom, 0px)",
            left: 0,
            width: 72,
            height: 72,
            zIndex: 4,
            background: "transparent",
          }}
          aria-hidden="true"
        />
      )}

      {/* HOME */}
      {screen === "home" && (
        <HomeScreen
          username={storageUserId}
          onSettings={() => setSettingsOpen(true)}
          hearts={{
            current: heartsCurrent,
            max: heartsMax,
            lastRegenAt,
            nextRefreshAt: nextHeartsRefreshAt,
          }}
          coins={coins}
          onShop={openStoreFromScreen}
          onStart={handleHomeStart}
          classicStats={classicStats}
          timetrialStats={timetrialStats}
          maxLevelsByMode={homeMaxLevels}
          t={t}
          lang={lang}
          setHints={setHints}
          progress={progress}
          dailySpinLastClaimedAt={
            cooldowns?.dailySpin?.last_claimed_at || null
          }
          onDailySpinClaim={handleDailySpinClaim}
          loggedIn={loggedIn}
          onAuthRequest={openAuth}
          showEntryCtas={!loggedIn && !guestSessionActive}
          onPlayAsGuest={handleStartGuest}
          i18nAuditEnabled={debugOverlayEnabled && showDebugScreen}
        />
      )}

      {/* LOCAL PACKS */}
      {hasAppSession && screen === "local-packs" && (
        <>
          <Header
            showBack
            onBack={goHome}
            hearts={{
              current: heartsCurrent,
              max: heartsMax,
              lastRegenAt,
              nextRefreshAt: nextHeartsRefreshAt,
            }}
            username={storageUserId}
            onSettings={() => setSettingsOpen(true)}
            showHearts
            t={t}
            lang={lang}
            coins={coins}
            onCoinsClick={openStoreFromScreen}
          />
          <div style={{ padding: "12px 16px", maxWidth: 980, margin: "0 auto" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#fff",
                }}
              >
                {localFlagsLabel}
              </div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.95)",
                  border: "1px solid #e2e8f0",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0f172a",
                  boxShadow: "0 6px 14px rgba(15, 23, 42, 0.08)",
                }}
                aria-label="Total stars earned"
              >
                <span>{localPackStarsTotal}</span>
                <span style={{ fontSize: 14, lineHeight: 1 }}>★</span>
              </div>
            </div>
            <LocalPacksGrid
              packs={LOCAL_PACKS}
              progress={progress}
              t={t}
              lang={lang}
              onPackClick={handleLocalPackSelect}
            />
          </div>
        </>
      )}

      {/* LOCAL PACK LEVELS */}
      {hasAppSession && screen === "local-pack-levels" && (
        <>
          <Header
            showBack
            onBack={() => setScreen("local-packs")}
            hearts={{
              current: heartsCurrent,
              max: heartsMax,
              lastRegenAt,
              nextRefreshAt: nextHeartsRefreshAt,
            }}
            username={storageUserId}
            onSettings={() => setSettingsOpen(true)}
            showHearts
            t={t}
            lang={lang}
            coins={coins}
            onCoinsClick={openStoreFromScreen}
          />
          <LocalPackLevelsScreen
            t={t}
            lang={lang}
            pack={activeLocalPack}
            levels={localPackLevels}
            progress={progress}
            onLockedAttempt={(info) => setLockInfo(info)}
            onLevelClick={(level) => {
              setMode("local");
              setLevelId(level.id);
              setScreen("game");
            }}
          />
        </>
      )}

      {/* LEVELS */}
      {hasAppSession && screen === "levels" && (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Header
            showBack
            onBack={goHome}
            hearts={{
              current: heartsCurrent,
              max: heartsMax,
              lastRegenAt,
              nextRefreshAt: nextHeartsRefreshAt,
            }}
            username={storageUserId}
            onSettings={() => setSettingsOpen(true)}
            showHearts
            t={t}
            lang={lang}
            coins={coins}
            onCoinsClick={openStoreFromScreen}
          />
          <LevelScreen
            t={t}
            lang={lang}
            onLevelClick={onLevelClick}
            onLockedAttempt={(info) => setLockInfo(info)}
            username={storageUserId}
            mode={mode}
            progress={progress}
          />
        </div>
      )}

      {/* GAME */}
      {hasAppSession && screen === "game" && (
        <>
          <Header
            showBack
            onBack={handleGameBack}
            hearts={{
              current: heartsCurrent,
              max: heartsMax,
              lastRegenAt,
              nextRefreshAt: nextHeartsRefreshAt,
            }}
            username={storageUserId}
            onSettings={() => setSettingsOpen(true)}
            showHearts={false}
            t={t}
            lang={lang}
            coins={coins}
            disableCoinsClick
          />
          <GameScreen
            t={t}
            lang={lang}
            FLAGS={FLAGS}
            mode={mode}
            activeLocalPack={activeLocalPack}
            levelId={levelId}
            levelLabel={localLevelLabel}
            levels={activeLevels}
            currentStars={storedStars}
            storedStars={storedStars}
            onProgressUpdate={updateProgressAfterLevel}
            onRunLost={onRunLost}
            heartsCurrent={heartsCurrent}
            onNoLives={() => setNoLivesOpen(true)}
            soundCorrect={soundCorrect}
            soundWrong={soundWrong}
            onBack={handleGameBack}
            onNextLevel={handleNextLevel}
            onShop={openStoreFromScreen}
            onMainMenu={goHome}
            starsFromLives={starsFromLives}
            hints={hints}
            setHints={setHints}
            activeUser={storageUserId}
            username={storageUserId}
            // GameScreen sends coin *delta* (e.g. +100), not absolute total
            onCoinsChange={(delta) =>
              applyCoinsUpdate((prev) => {
                const d = Number(delta) || 0;
                const next = Math.max(0, prev + d);
                return next;
              })
            }
            onGameplayDiagnostics={handleGameplayDiagnostics}
            onQuestionFlowDiagnostics={handleQuestionFlowDiagnostics}
            isGuestUser={guestSessionActive && !loggedIn}
            onGuestAuthRequest={openAuth}
          />
        </>
      )}

      {/* SHOP / STORE */}
      {hasAppSession && screen === "shop" && (
        <>
          {/* Header still shows hearts & coins but coins are DISPLAY only here */}
          <Header
            showBack
            onBack={() => setScreen(lastNonStoreScreen || "levels")}
            hearts={{
              current: heartsCurrent,
              max: heartsMax,
              lastRegenAt,
              nextRefreshAt: nextHeartsRefreshAt,
            }}
            username={storageUserId}
            onSettings={() => setSettingsOpen(true)}
            showHearts
            t={t}
            lang={lang}
            coins={coins}
          />
          <StoreScreen
            t={t}
            lang={lang}
            coins={coins}
            hints={hints}
            setHints={setHints}
            // Store passes an *absolute* new total
            onUpdateCoins={(next) => applyCoinsUpdate(next)}
            onBack={() => setScreen(lastNonStoreScreen || "levels")}
            hearts={heartsCurrent}
            maxHearts={heartsMax}
            onBuyHeartWithCoins={handleBuyHeartWithCoins}
            showPriceDebugOverlay={debugOverlayEnabled && showDebugScreen}
          />
        </>
      )}

      {/* Settings */}
      {settingsOpen && (
        <SettingsModal
          onClose={() => setSettingsOpen(false)}
          soundOn={soundOn}
          setSoundOn={setSoundOn}
          lang={lang}
          setLang={handleLanguageChange}
          activeUser={activeUser}
          setActiveUser={(u) => {
            setActiveUser(u);
            if (!u) {
              setActiveUserLabel("");
              setScreen("home");
            }
          }}
          activeUserLabel={activeUserLabel}
          setActiveUserLabel={setActiveUserLabel}
          setScreen={setScreen}
          LANGS={LANGS}
          t={t}
          onAuthRequest={openAuth}
          onRestorePurchases={handleRestorePurchases}
          isGuestMode={guestSessionActive && !loggedIn}
        />
      )}

      {/* modals */}
      {lockInfo && (
        <LockedModal
          lang={lang}
          info={lockInfo}
          onClose={() => setLockInfo(null)}
        />
      )}
      {noLivesOpen && (
        <NoLivesModal
          lastRegenAt={lastRegenAt}
          maxHearts={heartsMax}
          onClose={() => setNoLivesOpen(false)}
        />
      )}

      {guestPromptMilestone > 0 && guestSessionActive && !loggedIn && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(2, 6, 23, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 240,
            padding: 16,
          }}
        >
          <div
            style={{
              width: "min(420px, 100%)",
              background: "#fff",
              borderRadius: 20,
              padding: 18,
              boxShadow: "0 18px 40px rgba(15, 23, 42, 0.28)",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
              {t(lang, "guestPromptTitle")}
            </div>
            <div style={{ fontSize: 13, color: "#475569", marginTop: 8 }}>
              {ti(lang, "guestPromptBody", { milestone: guestPromptMilestone })}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 16,
              }}
            >
              <button
                onClick={() => {
                  setGuestPromptedMilestones((prev) =>
                    Array.from(new Set([...(Array.isArray(prev) ? prev : []), guestPromptMilestone]))
                  );
                  setGuestPromptMilestone(0);
                }}
                style={{
                  border: "1px solid #cbd5e1",
                  background: "#fff",
                  borderRadius: 10,
                  padding: "8px 12px",
                  cursor: "pointer",
                }}
              >
                {t(lang, "maybeLater")}
              </button>
              <button
                onClick={() => {
                  setGuestPromptedMilestones((prev) =>
                    Array.from(new Set([...(Array.isArray(prev) ? prev : []), guestPromptMilestone]))
                  );
                  setGuestPromptMilestone(0);
                  openAuth("signup");
                }}
                style={{
                  border: "1px solid #0b74ff",
                  background: "#0b74ff",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "8px 12px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {t(lang, "createAccount")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth */}
      {!loggedIn && authOpen && (
        <AuthModal
          lang={lang}
          t={t}
          onClose={closeAuth}
          tab={authTab}
          setTab={setAuthTab}
          users={users}
          setUsers={setUsers}
          onLoggedIn={async (u) => {
            const authAction = typeof u === "object" ? u.authAction : "";
            const nextId = typeof u === "object" ? u.id || "" : u;
            const nextLabel =
              typeof u === "object" ? u.label || u.id || "" : u;

            if (guestSessionActive && nextId) {
              await migrateGuestDataToAccount(nextId, authAction || "login");
            }

            setBackendLoaded(false);
            if (u && typeof u === "object") {
              setActiveUser(nextId);
              setActiveUserLabel(nextLabel);
            } else {
              setActiveUser(nextId);
              setActiveUserLabel(nextLabel);
            }
            setGuestSessionActive(false);
            setPendingAuthAction(authAction || "login");
            setScreen("home");
          }}
        />
      )}

      {!screen && (
        <div style={{ color: "white", textAlign: "center", marginTop: 100 }}>
          ⚠️ App loaded but no screen selected (state: {String(screen)})
        </div>
      )}

      {debugOverlayEnabled && showDebugScreen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
            background: "rgba(15, 23, 42, 0.92)",
            color: "white",
            overflowY: "auto",
            padding: 20,
          }}
        >
          <div
            style={{
              maxWidth: 900,
              margin: "0 auto",
              background: "rgba(15, 23, 42, 0.8)",
              borderRadius: 12,
              padding: 20,
              border: "1px solid rgba(148, 163, 184, 0.4)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 700 }}>Debug Logs</div>
              <button
                onClick={() => setShowDebugScreen(false)}
                style={{
                  background: "rgba(148, 163, 184, 0.2)",
                  border: "1px solid rgba(148, 163, 184, 0.4)",
                  color: "white",
                  padding: "6px 12px",
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                Close
              </button>
            </div>
            <IapDiagnosticsPanel visible={showDebugScreen} />
            <div
              style={{
                border: "1px solid rgba(148, 163, 184, 0.3)",
                borderRadius: 10,
                padding: 12,
                marginBottom: 12,
                background: "rgba(15, 23, 42, 0.65)",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Audio diagnostics</div>
              <div style={{ fontSize: 12, color: "#cbd5f5", lineHeight: 1.5 }}>
                <div>platform: {typeof Capacitor?.getPlatform === "function" ? Capacitor.getPlatform() : "web"}</div>
                <div>visibilityState: {typeof document !== "undefined" ? document.visibilityState : "unknown"}</div>
                <div>hasUserInteracted: {String(hasUserInteracted)}</div>
                <div>audio system used: WebAudio</div>
                <div>AudioContext state: {audioCtxRef.current?.state || "unavailable"}</div>
                <div>AudioContext sampleRate: {audioCtxRef.current?.sampleRate || "unavailable"}</div>
                <div>loaded sounds: 0</div>
                <div>currently playing sounds: {activeTonesRef.current}</div>
                <div>sfxEnabled: {String(soundOn)}</div>
                <div>musicEnabled: {String(soundOn)}</div>
                <div>sfxVolume: {Number(volume).toFixed(2)}</div>
                <div>musicVolume: {Number(volume).toFixed(2)}</div>
                <div>masterVolume: {Number(volume).toFixed(2)}</div>
                <div>global mute path active: {String(!soundOn || Number(volume) <= 0)}</div>
                <div>AVAudioSession category: {audioSessionInfo.category}</div>
                <div>AVAudioSession mode: {audioSessionInfo.mode}</div>
                <div>AVAudioSession active: {String(audioSessionInfo.active)}</div>
                <div>unlock result: {audioUnlockResult ? `${audioUnlockResult.ok ? "success" : "fail"} - ${audioUnlockResult.message}` : "not run"}</div>
                <div>last tick: {audioDiagTick}</div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <button
                  onClick={() => unlockAudio()}
                  style={{
                    background: "rgba(16, 185, 129, 0.2)",
                    border: "1px solid rgba(16, 185, 129, 0.5)",
                    color: "white",
                    padding: "6px 12px",
                    borderRadius: 8,
                    fontWeight: 600,
                  }}
                >
                  Unlock Audio
                </button>
                <button
                  onClick={() => {
                    soundCorrect();
                    pushAudioDiagLog("Test SFX pressed");
                  }}
                  style={{
                    background: "rgba(59, 130, 246, 0.2)",
                    border: "1px solid rgba(59, 130, 246, 0.5)",
                    color: "white",
                    padding: "6px 12px",
                    borderRadius: 8,
                    fontWeight: 600,
                  }}
                >
                  Test SFX
                </button>
                <button
                  onClick={() => {
                    testMusic();
                    pushAudioDiagLog("Test Music pressed");
                  }}
                  style={{
                    background: "rgba(168, 85, 247, 0.2)",
                    border: "1px solid rgba(168, 85, 247, 0.5)",
                    color: "white",
                    padding: "6px 12px",
                    borderRadius: 8,
                    fontWeight: 600,
                  }}
                >
                  Test Music
                </button>
              </div>
              <div
                style={{
                  marginTop: 10,
                  background: "rgba(2, 6, 23, 0.7)",
                  borderRadius: 8,
                  padding: 8,
                  fontSize: 12,
                  maxHeight: 140,
                  overflowY: "auto",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {audioDiagLogs.length === 0 ? "No audio logs yet" : audioDiagLogs.join("\n")}
              </div>
            </div>
            <div
              style={{
                border: "1px solid rgba(148, 163, 184, 0.3)",
                borderRadius: 10,
                padding: 12,
                marginBottom: 12,
                background: "rgba(15, 23, 42, 0.65)",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Gameplay diagnostics</div>
              {gameplayDiagnostics ? (
                <div style={{ fontSize: 12, color: "#cbd5f5", lineHeight: 1.5 }}>
                  <div>Level preload: {Math.round(gameplayDiagnostics.preloadMs || 0)} ms</div>
                  <div>Time to first question: {Math.round(gameplayDiagnostics.firstQuestionMs || 0)} ms</div>
                  <div>Network calls during preload: {Number(gameplayDiagnostics.preloadNetworkCalls) || 0}</div>
                  <div>Network calls during level: {Number(gameplayDiagnostics.gameplayNetworkCalls) || 0}</div>
                  <div style={{ marginTop: 8, fontWeight: 700 }}>Flag Diagnostics</div>
                  <div>totalFlags: {Number(gameplayDiagnostics.totalFlags) || 0}</div>
                  <div>excludedPermanent: {Number(gameplayDiagnostics.excludedPermanent) || 0}</div>
                  <div>excludedBrokenSession: {Number(gameplayDiagnostics.excludedBrokenSession) || 0}</div>
                  <div>lastFailedFlagCode: {gameplayDiagnostics.lastFailedFlagCode || "-"}</div>
                  <div>lastFailedFlagUrl: {gameplayDiagnostics.lastFailedFlagUrl || "-"}</div>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "#cbd5f5" }}>No gameplay run recorded yet.</div>
              )}
            </div>
            <div
              style={{
                border: "1px solid rgba(148, 163, 184, 0.3)",
                borderRadius: 10,
                padding: 12,
                marginBottom: 12,
                background: "rgba(15, 23, 42, 0.65)",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Question progression diagnostics</div>
              {questionFlowDiagnostics ? (
                <div style={{ fontSize: 12, color: "#cbd5f5", lineHeight: 1.5 }}>
                  <div>route: {questionFlowDiagnostics.route || "unknown"}</div>
                  <div>screen: {questionFlowDiagnostics.screen || "unknown"}</div>
                  <div>mode: {questionFlowDiagnostics.mode || "unknown"}</div>
                  <div>levelId: {questionFlowDiagnostics.levelId || "-"}</div>
                  <div>questionIndex: {Number(questionFlowDiagnostics.questionIndex) || 0}</div>
                  <div>questionCount: {Number(questionFlowDiagnostics.questionCount) || 0}</div>
                  <div>questionCode: {questionFlowDiagnostics.questionCode || "-"}</div>
                  <div>selectedAnswer: {questionFlowDiagnostics.selectedAnswer || "none"}</div>
                  <div>uiHasSelectedAnswer: {String(questionFlowDiagnostics.uiHasSelectedAnswer)}</div>
                  <div>isValidationInProgress: {String(questionFlowDiagnostics.isValidationInProgress)}</div>
                  <div>isWaitingAsync: {String(questionFlowDiagnostics.isWaitingAsync)}</div>
                  <div>isInputLocked: {String(questionFlowDiagnostics.isInputLocked)}</div>
                  <div>isLoading: {String(questionFlowDiagnostics.isLoading)}</div>
                  <div>isAnimatingTransition: {String(questionFlowDiagnostics.isAnimatingTransition)}</div>
                  <div>isFetchingNextQuestion: {String(questionFlowDiagnostics.isFetchingNextQuestion)}</div>
                  <div>actionInFlight: {String(questionFlowDiagnostics.actionInFlight)}</div>
                  <div>resolvedTapTarget: {questionFlowDiagnostics.resolvedTapTarget || "none"}</div>
                  <div>resolvedOptionIndex: {Number(questionFlowDiagnostics.resolvedOptionIndex ?? -1)}</div>
                  <div>resolvedAnswerId: {questionFlowDiagnostics.resolvedAnswerId || "none"}</div>
                  <div>lastGuardBlockedReason: {questionFlowDiagnostics.lastGuardBlockedReason || "none"}</div>
                  <div>boosterState: {questionFlowDiagnostics.boosterState || "none"}</div>
                  <div>lastProgressionError: {questionFlowDiagnostics.lastProgressionError || "none"}</div>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "#cbd5f5" }}>No question-progression diagnostics yet.</div>
              )}
            </div>
            <div
              style={{
                border: "1px solid rgba(148, 163, 184, 0.3)",
                borderRadius: 10,
                padding: 12,
                marginBottom: 12,
                background: "rgba(15, 23, 42, 0.65)",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Reset-password diagnostics</div>
              <div style={{ fontSize: 12, color: "#cbd5f5", lineHeight: 1.5 }}>
                <div>lastDeepLinkReceived: {resetDiagnostics.lastDeepLinkReceived || "-"}</div>
                <div>parsedType: {resetDiagnostics.parsedType || "unknown"}</div>
                <div>paramsPresent: access_token={String(resetDiagnostics.hasAccessToken)}, refresh_token={String(resetDiagnostics.hasRefreshToken)}, code={String(resetDiagnostics.hasCode)}, token_hash={String(resetDiagnostics.hasTokenHash)}</div>
                <div>recoveryPath: {resetDiagnostics.recoveryPath || "none"}</div>
                <div>didExchangeOrSetSessionSucceed: {String(resetDiagnostics.didExchangeOrSetSessionSucceed)}</div>
                <div>sessionPresentAfterRecovery: {String(resetDiagnostics.sessionPresentAfterRecovery)}</div>
                <div>lastRecoveryHandlingResult: {resetDiagnostics.recoveryResult || "none"}</div>
                <div>lastResetError: {resetDiagnostics.lastResetError || "-"}</div>
              </div>
              <button
                onClick={() => {
                  const line = [
                    `lastDeepLinkReceived=${resetDiagnostics.lastDeepLinkReceived || ""}`,
                    `parsedType=${resetDiagnostics.parsedType || "unknown"}`,
                    `hasAccessToken=${String(resetDiagnostics.hasAccessToken)}`,
                    `hasRefreshToken=${String(resetDiagnostics.hasRefreshToken)}`,
                    `hasCode=${String(resetDiagnostics.hasCode)}`,
                    `hasTokenHash=${String(resetDiagnostics.hasTokenHash)}`,
                    `recoveryPath=${resetDiagnostics.recoveryPath || "none"}`,
                    `didExchangeOrSetSessionSucceed=${String(resetDiagnostics.didExchangeOrSetSessionSucceed)}`,
                    `sessionPresentAfterRecovery=${String(resetDiagnostics.sessionPresentAfterRecovery)}`,
                    `lastRecoveryHandlingResult=${resetDiagnostics.recoveryResult || "none"}`,
                    `lastResetError=${resetDiagnostics.lastResetError || ""}`,
                  ].join("; ");
                  navigator?.clipboard?.writeText?.(line).catch(() => {});
                }}
                style={{ marginTop: 8 }}
              >
                Copy diagnostics
              </button>
            </div>
            {debugLogs.length === 0 && (
              <div style={{ color: "#cbd5f5" }}>
                No stored logs yet.
              </div>
            )}
            {debugLogs.map((log) => (
              <div
                key={log.id || `${log.timestamp}-${log.message}`}
                style={{
                  border: "1px solid rgba(148, 163, 184, 0.3)",
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 12,
                  background: "rgba(15, 23, 42, 0.65)",
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  {log.level ? `[${log.level}] ` : ""}
                  {log.message || "Unknown log"}
                </div>
                <div style={{ fontSize: 12, color: "#cbd5f5" }}>
                  {log.timestamp || "Unknown time"}
                </div>
                <div style={{ fontSize: 12, color: "#cbd5f5" }}>
                  Route: {log.route || "unknown"}
                </div>
                {log.source && (
                  <div style={{ fontSize: 12, color: "#cbd5f5" }}>
                    Source: {log.source}
                  </div>
                )}
                {log.stack && (
                  <pre
                    style={{
                      marginTop: 10,
                      padding: 10,
                      background: "rgba(2, 6, 23, 0.75)",
                      borderRadius: 8,
                      fontSize: 12,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {log.stack}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {debugOverlayEnabled && showDebugOverlay && debugError && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(15, 23, 42, 0.92)",
            color: "white",
            padding: 20,
            overflowY: "auto",
          }}
        >
          <div
            style={{
              maxWidth: 900,
              margin: "0 auto",
              background: "rgba(15, 23, 42, 0.85)",
              borderRadius: 12,
              padding: 20,
              border: "1px solid rgba(148, 163, 184, 0.4)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                Debug Overlay
              </div>
              <button
                onClick={() => setShowDebugOverlay(false)}
                style={{
                  background: "rgba(148, 163, 184, 0.2)",
                  border: "1px solid rgba(148, 163, 184, 0.4)",
                  color: "white",
                  padding: "6px 12px",
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                Close
              </button>
            </div>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              {debugError.message || "Unknown error"}
            </div>
            {(appInfo.version || appInfo.build) && (
              <div style={{ fontSize: 12, color: "#cbd5f5" }}>
                App: {appInfo.version || "unknown"} (
                {appInfo.build || "unknown"})
              </div>
            )}
            <div style={{ fontSize: 12, color: "#cbd5f5" }}>
              Route: {debugError.route || "unknown"}
            </div>
            {debugError.source && (
              <div style={{ fontSize: 12, color: "#cbd5f5" }}>
                Source: {debugError.source}
              </div>
            )}
            {debugError.stack && (
              <pre
                style={{
                  marginTop: 12,
                  padding: 12,
                  background: "rgba(2, 6, 23, 0.75)",
                  borderRadius: 8,
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {debugError.stack}
              </pre>
            )}
            {debugLogs.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>
                  Recent logs
                </div>
                <div
                  style={{
                    maxHeight: 220,
                    overflowY: "auto",
                    background: "rgba(2, 6, 23, 0.6)",
                    borderRadius: 8,
                    padding: 10,
                    fontSize: 12,
                    lineHeight: 1.4,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {debugLogs
                    .slice(0, 20)
                    .map((log) => {
                      const level = log.level
                        ? log.level.toUpperCase()
                        : "LOG";
                      return `[${level}] ${log.timestamp || ""} ${
                        log.message || ""
                      }`;
                    })
                    .join("\n")}
                </div>
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <button
                onClick={handleCopyDebugError}
                style={{
                  background: "#38bdf8",
                  color: "#0f172a",
                  border: "none",
                  padding: "10px 16px",
                  borderRadius: 10,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
